/* ttg-baukasten.js — Matten-Baukasten (fm8, 15./16.10.2026).
 *
 * Eigenständige Schicht NEBEN Engine, Familien- und Füße-Konfigurator (null Diff an
 * bestehenden Dateien). Vorbild für Warenkorb, Vorschaubild und Druckvertrag ist
 * ttg-family.js (dort ist der Weg bis zur Druck-PDF live belegt).
 *
 * Daten statt Code: Texte, Schriften, Farben, Mitglieder-Typen, Grafiken und alle
 * Maße einer Matte stehen im Produkt-Metafeld custom.matten_baukasten (version 1).
 * Der Code kennt nur die Anordnungstypen
 *     keine · reihe-bild · reihe-figur · fuesse · herzen · pfoten-namen · namenzeile · rollenliste
 * Textmatten (19.10.2026, additiv — ohne die neuen Schlüssel bleibt jede Matte byte-gleich):
 *  - Option „Farbe“ optional: führt das Produkt keine, zählt für die Variante nur die Größe; die Mattenfarbe
 *    (bzw. der Hintergrund, cfg.farbe_label) wird dann im Baukasten gewählt und als Eigenschaft mitgegeben (T1).
 *  - cfg.bilder: feste Grafiken als eigene Ebene in jeder Anordnung (ebene unten|oben, optional je Mattenfarbe) (T2).
 *  - Feld fest:true: feste Textzeile (Text aus f.text), kein Eingabefeld, nicht in den Eigenschaften (T3).
 *  - cfg.textfarbe: Textfarbe je Wert einer Variantenoption (z. B. „Schriftfarbe“), sonst als Eigenschaft (T4).
 *  - cfg.felder[].defaultNoText (Opt-out wie die Engine, 20.10.2026): gesetzt (true/false) an einem nicht-pflichtigen Feld zeigt das
 *    Kästchen „Kein Text gewünscht“ unter dem Feld (Startzustand = Wert). Angehakt: Feld gedimmt (nicht gesperrt — Klick ins Feld
 *    hebt den Haken auf), kein Text in Vorschau und Druckvertrag, Eigenschaft <Label> = „Kein Text“, keine Beispiel-Nachfrage.
 *    Abhaken holt den vorherigen Wert zurück. Ohne Schlüssel byte-gleich.
 *  - cfg.felder[].beispiel: true (Pilot 2): Vorgabe des Feldes gilt als Beispiel — Etikett „Beispiel“ und freundliche Nachfrage am Warenkorb
 *    („Du hast noch Beispielnamen …“), solange der Wert unverändert ist; blockiert nie, nie bei festen Feldern; ohne Schalter unverändert.
 *  - cfg.zeichen_fehlen {Schriftschlüssel: Zeichen}: Zeichensperre je Schrift — erlaubt ist BK_ZEICHEN ohne die Zeichen,
 *    die der Schrift des Feldes fehlen (gemessen am Schriftfile, scripts/textmatten/zeichen-fehlen.py) (Teil 2).
 * und rechnet sie 1:1 nach der Referenz designs.py/lib.py aus dem fm8-Paket
 * (Designraum 1500 × 1000 px = Matte 3:2; Vertrag in mm × 0,4 = 600 × 400).
 *
 * Aufbau:
 *  - Pure Funktionen (Node-fähig, Messfunktion wird injiziert): Reihe, Namenszeilen,
 *    Textanpassung, Szene, Druckvertrag, Warenkorb-Eigenschaften — getestet in
 *    test/baukasten-*.mjs.
 *  - Custom Element <ttg-baukasten> (nur im Browser): Canvas-Vorschau auf der Bühne mit
 *    Gummirand, Bedienung, Varianten- und Property-Sync ans native Produktformular
 *    (Cart-Handover Variante B), Vorschaubild über den globalen Uploader.
 *
 * Vektorgrafik (Herz, Pfote, Pfeil, Figuren von hinten) kommt aus
 * assets/ttg-baukasten-figuren.js — 1:1-Port von figures.py; der Druck benutzt die
 * Python-Kopie (ttg-mattenrender mattenrender/baukasten_figuren.py). Gleichheit: test/
 * baukasten-figuren-gleichheit.mjs.
 */

export const BK = {
  W: 1500, H: 1000,
  MM: 0.4,                 // mm je Designpixel (600 / 1500)
  ZEILENHOEHE: 1.05,       // lib.py: .t span{line-height:1.05}
  FIT_FAKTOR: 0.97,        // lib.py FIT_JS: s *= 0.97
  FIT_SCHRITTE: 80,
  TEXTFARBE: '#ffffff',
  // fm8-fix5: weicher Schatten auf Rasen (farben[x].schatten) — Designpixel; sigma = Gauß-Breite (Canvas shadowBlur = 2·sigma)
  SCHATTEN: { dx: 0, dy: 3, sigma: 5, alpha: 0.5 },
};

export const BK_STASH = 'ttg:pendingPreview';            // wie ttg-family.js / ttg-preview-uploader.js
export const BK_PREVIEW_TIMEOUT_MS = 8000;
export const BK_GROESSEN = { '40x60': { w: 7087, h: 4724 }, '50x75': { w: 8858, h: 5906 } };
export const BK_OPTION = { groesse: 'Größe', farbe: 'Farbe' };
// Opt-out „kein Text“ (wie ttg-engine.js: strings.no_text / no_text_cta, Section ttg-customizer.liquid)
export const BK_KEIN_TEXT = 'Kein Text';
export const BK_KEIN_TEXT_CTA = 'Kein Text gewünscht';
// Feld mit Opt-out: Schlüssel defaultNoText gesetzt, nicht Pflicht, nicht fest (Engine: Kästchen nur an optionalen Texten)
export const hatOptOut = (f) => !!f && typeof f.defaultNoText === 'boolean' && !f.pflicht && !f.fest;
const istOhneText = (state, f) => hatOptOut(f) && !!(state && state.ohneText && state.ohneText[f.id]);

// Einfarbige Glyphen für Herz und Pfote im Wunschtext — byte-gleich mit TTG_EMOJI_GLYPHS
// (ttg-engine.js) und FOTO_EMOJI_PFADE (ttg-mattenrender render.py). 24×24-Einheitsraum.
export const BK_EMOJI = {
  '❤️': 'M12 21.35C6.4 17.36 2 13.8 2 9.6 2 6.5 4.42 4 7.5 4c1.74 0 3.41.81 4.5 2.09A5.99 5.99 0 0 1 16.5 4C19.58 4 22 6.5 22 9.6c0 4.2-4.4 7.76-10 11.75z',
  '🐾': 'M16.12 21.2Q15.59 21.2 15.25 20.81Q14.9 20.43 14.9 19.83Q14.9 19.03 15.32 18.36Q15.74 17.69 16.45 17.29Q17.17 16.89 18.03 16.89Q18.77 16.89 19.42 17.18Q20.07 17.48 20.48 17.95Q20.88 18.43 20.88 18.99Q20.88 19.54 20.48 19.82Q20.08 20.11 19.39 20.11Q18.73 20.11 18.27 20.27Q17.81 20.44 17.46 20.65Q17.11 20.87 16.79 21.03Q16.47 21.2 16.12 21.2ZM12.94 18.9Q12.53 18.9 12.17 18.64Q11.81 18.37 11.58 17.96Q11.35 17.55 11.35 17.12Q11.35 16.69 11.58 16.41Q11.82 16.13 12.24 16.13Q12.67 16.13 13.04 16.39Q13.4 16.64 13.63 17.05Q13.85 17.46 13.85 17.91Q13.85 18.36 13.6 18.63Q13.34 18.9 12.94 18.9ZM4 9.41Q3.62 9.41 3.28 9.17Q2.93 8.92 2.72 8.53Q2.5 8.13 2.5 7.7Q2.5 7.22 2.78 6.91Q3.05 6.61 3.47 6.61Q3.84 6.61 4.2 6.84Q4.56 7.07 4.79 7.47Q5.02 7.86 5.02 8.32Q5.02 8.79 4.73 9.1Q4.45 9.41 4 9.41ZM6.63 12.19Q6.03 12.19 5.78 11.84Q5.53 11.48 5.53 10.98Q5.53 10.27 5.96 9.59Q6.39 8.9 7.1 8.45Q7.8 8 8.62 8Q9.27 8 9.94 8.29Q10.61 8.57 11.06 9.05Q11.52 9.53 11.52 10.11Q11.52 11.27 9.77 11.27Q9.03 11.27 8.59 11.41Q8.15 11.54 7.86 11.73Q7.57 11.91 7.29 12.05Q7.02 12.19 6.63 12.19ZM14.8 16.51Q14.4 16.51 14.05 16.25Q13.7 16 13.48 15.6Q13.27 15.2 13.27 14.78Q13.27 14.28 13.55 13.96Q13.84 13.64 14.28 13.64Q14.69 13.64 15.04 13.9Q15.39 14.15 15.6 14.55Q15.82 14.94 15.82 15.37Q15.82 15.84 15.54 16.18Q15.26 16.51 14.8 16.51ZM6.24 7.41Q5.72 7.41 5.31 6.87Q4.9 6.33 4.9 5.62Q4.9 5.03 5.19 4.65Q5.48 4.26 5.92 4.26Q6.28 4.26 6.58 4.51Q6.89 4.77 7.08 5.18Q7.27 5.59 7.27 6.06Q7.27 6.63 6.98 7.02Q6.68 7.41 6.24 7.41ZM17.46 15.4Q16.94 15.4 16.61 14.9Q16.28 14.4 16.28 13.8Q16.28 13.26 16.55 12.83Q16.82 12.39 17.31 12.39Q17.84 12.39 18.17 12.9Q18.5 13.41 18.5 14.01Q18.5 14.54 18.22 14.97Q17.95 15.4 17.46 15.4ZM9.14 6.78Q8.63 6.78 8.28 6.33Q7.92 5.88 7.92 5.24Q7.92 4.64 8.23 4.22Q8.53 3.8 8.96 3.8Q9.47 3.8 9.83 4.25Q10.19 4.7 10.19 5.33Q10.19 5.93 9.88 6.36Q9.57 6.78 9.14 6.78ZM11.86 7.7Q11.4 7.7 11.09 7.31Q10.77 6.92 10.77 6.35Q10.77 5.75 11.13 5.31Q11.48 4.87 11.96 4.87Q12.42 4.87 12.73 5.26Q13.04 5.65 13.04 6.21Q13.04 6.82 12.69 7.26Q12.35 7.7 11.86 7.7ZM20.43 15.83Q19.91 15.83 19.58 15.39Q19.25 14.94 19.25 14.39Q19.25 13.84 19.57 13.43Q19.88 13.03 20.36 13.03Q20.87 13.03 21.18 13.47Q21.5 13.92 21.5 14.47Q21.5 15.01 21.19 15.42Q20.89 15.83 20.43 15.83Z',
};
const VS16 = '️';

// Erlaubte Zeichen (R6): Buchstaben, die ALLE zehn Baukasten-Schriften tragen (gemessen an den
// Untermengen-Dateien, 322 gemeinsame Zeichen), Ziffern, Leerzeichen und . , - & ' ’ ! ? : ·
// Herz und Pfote laufen als Glyphe (BK_EMOJI). ♥ wird zum Herz (gleiche Bedeutung, druckbar).
export const BK_BUCHSTABEN = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyzÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖØÙÚÛÜÝÞßàáâãäåæçèéêëìíîïðñòóôõöøùúûüýþÿĀāĂăĄąĆćĈĉĊċČčĎďĐđĒēĔĕĖėĘęĚěĜĝĞğĠġĢģĤĥĦħĨĩīĬĭĮįİıĴĵĶķĸĹĺĻļĽľĿŀŁłŃńŅņŇňŊŋŌōŎŏŐőŒœŔŕŖŗŘřŚśŜŝŞşŠšŢţŤťŦŧŨũŪūŬŭŮůŰűŲųŴŵŶŷŸŹźŻżŽž';
export const BK_ZEICHEN = BK_BUCHSTABEN + '0123456789 .,-&\'’!?:·';

// ---------- Zeichen und Segmente ----------

// Zeile -> [{art:'text'|'emoji', wert}] — VS16-tolerant wie _foto_segmente (render.py).
export function segmente(text) {
  const out = [];
  let buf = '';
  const s = String(text);
  for (let i = 0; i < s.length;) {
    let treffer = null;
    for (const e of Object.keys(BK_EMOJI)) {
      const bare = e.replace(VS16, '');
      if (s.startsWith(e, i)) { treffer = [e, e.length]; break; }
      if (bare && s.startsWith(bare, i)) {
        treffer = [e, bare.length + (s.slice(i + bare.length, i + bare.length + 1) === VS16 ? 1 : 0)];
        break;
      }
    }
    if (treffer) {
      if (buf) { out.push({ art: 'text', wert: buf }); buf = ''; }
      out.push({ art: 'emoji', wert: treffer[0] });
      i += treffer[1];
    } else { buf += s[i]; i += 1; }
  }
  if (buf) out.push({ art: 'text', wert: buf });
  return out;
}

// Eingabe prüfen (R6). Rückgabe { wert, fehler } — wert ist die Eingabe mit ♥→❤️ und ohne
// Zeilenumbrüche jenseits der erlaubten Zeilenzahl; der Umbruch des Kunden bleibt sonst unverändert.
// fehlen (Textmatten Teil 2): Zeichen, die der Schrift des Feldes fehlen — gesperrt wie fremde Zeichen, eigene Meldung.
export function pruefeEingabe(roh, { max = 30, zeilen = 1, fehlen = '' } = {}) {
  let wert = String(roh == null ? '' : roh).replace(/\r\n?/g, '\n').replace(/♥️?/g, '❤️');
  const teile = wert.split('\n');
  if (teile.length > zeilen) wert = teile.slice(0, zeilen).join('\n') + ' ' + teile.slice(zeilen).join(' ');
  const fremd = new Set();
  const gesperrt = new Set();
  let laenge = 0;
  for (const zeile of wert.split('\n')) {
    for (const sg of segmente(zeile)) {
      if (sg.art === 'emoji') { laenge += 1; continue; }
      for (const z of sg.wert) {
        laenge += 1;
        if (!BK_ZEICHEN.includes(z)) fremd.add(z);
        else if (fehlen && fehlen.includes(z)) gesperrt.add(z);
      }
    }
  }
  let fehler = '';
  if (gesperrt.size && !fremd.size) {
    fehler = 'Diese Zeichen gibt es in der Schrift dieser Matte leider nicht: ' + [...gesperrt].join(' ');
  } else if (fremd.size) {
    const liste = [...fremd].join(' ');
    fehler = /\p{Extended_Pictographic}/u.test(liste)
      ? 'Dieses Symbol können wir leider nicht drucken: ' + liste + ' — erlaubt sind ❤️ und 🐾.'
      : 'Diese Zeichen können wir leider nicht drucken: ' + liste;
  } else if (laenge > max) {
    fehler = 'Bitte höchstens ' + max + ' Zeichen.';
  }
  return { wert, fehler, laenge };
}

// Textmatten Teil 2: Zeichen, die der Schrift fehlen (aus dem Metafeld), sonst ''
export function fehlendeZeichen(cfg, font) {
  const z = (cfg && cfg.zeichen_fehlen) || null;
  return (z && font && z[font]) || '';
}

export function zeichenZahl(text) {
  let n = 0;
  for (const zeile of String(text || '').split('\n')) for (const sg of segmente(zeile)) n += sg.art === 'emoji' ? 1 : [...sg.wert].length;
  return n;
}

// ---------- Layout-Bausteine (1:1 designs.py) ----------

// row_positions: Reihe zentriert; passt sie nicht, werden alle Elemente gleichmäßig kleiner
// (nie unter minScale). Rückgabe { xs, sc }.
export function rowPositions(widths, x0 = 140, x1 = 1360, gap = 30, maxScale = 1.0, minScale = 0.55) {
  const n = widths.length;
  const total = widths.reduce((a, b) => a + b, 0) + gap * (n - 1);
  const sc = Math.max(minScale, Math.min(maxScale, (x1 - x0) / total));
  const tw = total * sc;
  let x = (x0 + x1) / 2 - tw / 2;
  const xs = [];
  for (const w of widths) { xs.push(x + w * sc / 2); x += (w + gap) * sc; }
  return { xs, sc };
}

// namen_zeilen: Namen mit Punkt. Eine Zeile, sonst zwei Zeilen (ausgewogen nach Zeichen).
export function namenZeilen(names, sep = ' · ', maxLine = 26) {
  const full = names.join(sep);
  if (full.length <= maxLine || names.length < 3) return [full];
  let best = null;
  for (let k = 1; k < names.length; k++) {
    const a = names.slice(0, k).join(sep), b = names.slice(k).join(sep);
    const sc = Math.max(a.length, b.length);
    if (best === null || sc < best[0]) best = [sc, [a, b]];
  }
  return best[1];
}

// ---------- Text: Messen, Einpassen, Grundlinie ----------
//
// M (Messung, injiziert): {
//   breite(font, px, str)  -> Vorschubbreite in px (Canvas measureText().width)
//   tinte(font, px, str)   -> { l, r } Tintenausdehnung links/rechts vom Startpunkt (actualBoundingBox)
//   metrik(font)           -> { a, d } Ober-/Unterlänge je 1 px Schriftgröße (fontBoundingBox)
// }

// Lauf einer Zeile: Positionen relativ zum Zeilenanfang + Gesamtbreite (inkl. Sperrung hinter
// dem letzten Zeichen — CSS letter-spacing). Gleiche Rechnung wie text_lauf (baukasten.py).
export function textLauf(M, font, px, text, lsEm) {
  const ls = (lsEm || 0) * px;
  const stueck = [];
  let x = 0;
  const glyph = px * 0.95;
  for (const sg of segmente(text)) {
    if (sg.art === 'emoji') { stueck.push({ art: 'emoji', wert: sg.wert, x }); x += glyph + ls; continue; }
    if (!ls) { stueck.push({ art: 'text', wert: sg.wert, x }); x += M.breite(font, px, sg.wert); continue; }
    const z = [...sg.wert];
    let prefix = '';
    z.forEach((c, i) => {
      prefix += c;
      const start = M.breite(font, px, prefix) - M.breite(font, px, c);
      stueck.push({ art: 'text', wert: c, x: x + start + i * ls });
    });
    x += M.breite(font, px, sg.wert) + z.length * ls;
  }
  return { stueck, breite: x };
}

// Tintenbreite einer Zeile (R7: echte Ink-Breite, nicht nur die Box)
export function tintenBreite(M, font, px, text, lsEm) {
  const { stueck, breite } = textLauf(M, font, px, text, lsEm);
  let l = 0, r = breite;
  for (const s of stueck) {
    if (s.art === 'emoji') { l = Math.min(l, s.x); r = Math.max(r, s.x + px * 0.95); continue; }
    const t = M.tinte(font, px, s.wert);
    l = Math.min(l, s.x - t.l); r = Math.max(r, s.x + t.r);
  }
  return { advance: breite, tinte: r - l };
}

// lib.py text(): Box (x,y)=Mitte; schrumpft gleichmäßig (×0,97), bis Breite UND Höhe passen.
// Die Box-Lage folgt lib.py exakt: left = x - w//2, top = y - h//2 (ganzzahlig).
export function passeText(M, f) {
  const zeilen = String(f.text).split('\n');
  const fitw = f.fitw != null ? f.fitw : f.w;
  const fith = f.fith != null ? f.fith : f.h;
  let s = f.px;
  // Gedrehter Text: lib.py misst mit getBoundingClientRect, also die achsparallele Hülle der GEDREHTEN Zeile —
  // dieselbe Rechnung hier, sonst wäre gedrehter Text größer als in der Referenz (gemessen M4 „Becker“, M6 Pfoten).
  const rad = f.rotate ? Math.abs(f.rotate) * Math.PI / 180 : 0;
  const misst = (sz) => {
    let w = 0;
    for (const z of zeilen) {
      if (!z) continue;
      const b = tintenBreite(M, f.font, sz, z, f.ls);
      w = Math.max(w, b.advance, b.tinte);
    }
    const h = zeilen.length * BK.ZEILENHOEHE * sz;
    if (!rad) return { w, h };
    return { w: w * Math.cos(rad) + h * Math.sin(rad), h: w * Math.sin(rad) + h * Math.cos(rad) };
  };
  let g = 0;
  let m = misst(s);
  while ((m.w > fitw || m.h > fith) && g < BK.FIT_SCHRITTE) { s *= BK.FIT_FAKTOR; m = misst(s); g += 1; }
  const left = f.x - Math.floor(f.w / 2);
  const top = f.y - Math.floor(f.h / 2);
  const cx = left + f.w / 2;
  const cy = top + f.h / 2;
  const { a, d } = M.metrik(f.font);
  const lh = BK.ZEILENHOEHE * s;
  const n = zeilen.length;
  const out = [];
  zeilen.forEach((z, i) => {
    if (!z.trim()) return;
    const zeilenTop = cy - (n * lh) / 2 + i * lh;
    const grundlinie = zeilenTop + (lh - (a + d) * s) / 2 + a * s;
    out.push({
      typ: 'text', id: f.id + (n > 1 ? '_' + (i + 1) : ''), text: z, font: f.font, size: s,
      x: f.align === 'left' ? left : cx, y: grundlinie, align: f.align === 'left' ? 'left' : 'center',
      ls: f.ls || 0, hex: f.hex || BK.TEXTFARBE, maxW: fitw,
      ...(f.rotate ? { rotate: f.rotate, rx: cx, ry: cy } : {}),
    });
  });
  return { elemente: out, px: s, schritte: g };
}

// name_text() aus designs.py: Box int(x), int(y), int(w), int(size*1.6)
export function nameText(x, y, w, name, font, size, extra = {}) {
  return {
    x: Math.trunc(x), y: Math.trunc(y), w: Math.trunc(w), h: Math.trunc(size * 1.6),
    text: extra.upper ? String(name).toUpperCase() : name, font, px: size, ls: extra.ls || 0,
    hex: extra.hex, id: extra.id,
  };
}

// B8 (nichts überlappt): Namensboxen einer Reihe auf den Abstand zum nächsten Namen begrenzen. lib.py gibt jeder Box
// die volle Spurbreite (M3 zusätzlich ±24 px Versatz) — lange Namen stießen dadurch aneinander (gemessen M3 lang:
// 15 mm Überlappung bei 50×75). Kappung: Breite ≤ Mittenabstand zum nächsten Nachbarn − luft (24 px ≈ 10–12 mm, sonst lesen sich Nachbarn als ein Name — gemessen M3 lang). Kurze Namen bleiben unberührt.
export function kappeNamen(namen, luft = 24) {
  return namen.map((n, i) => {
    let d = Infinity;
    if (i > 0) d = Math.min(d, n.x - namen[i - 1].x);
    if (i < namen.length - 1) d = Math.min(d, namen[i + 1].x - n.x);
    return d === Infinity ? n : { ...n, w: Math.max(40, Math.min(n.w, Math.trunc(d - luft))) };
  });
}

// ---------- Grafik-Hilfen ----------

// PNG aus dem Katalog an Fußpunkt/Mitte setzen. g = {canvas:[w,h], fusspunkt|mitte:[x,y]}
function bildAmPunkt(key, g, x, y, sc, extra = {}) {
  const p = g.fusspunkt || g.mitte;
  return {
    typ: 'bild', key, url: g.url, vorschau: g.vorschau || g.url,
    x: x - p[0] * sc, y: y - p[1] * sc, w: g.canvas[0] * sc, h: g.canvas[1] * sc, ...extra,
  };
}

function vektor(form, tx, ty, k, params) { return { typ: 'vektor', form, tx, ty, k, ...params }; }

// M9: Bodenlinie der Stiefel-Grafiken (boots.js G = 392) und Fußpunkt des Rotkehlchens (110×100, Füße bei 58/96)
const BK_STIEFEL_BODEN = 392;
const BK_ROTKEHLCHEN_FUSS = [58, 96];

// Symbolwert → Form: Teil vor „-" ("herz-rot" → "herz"). Farbe hängt am vollen Wert, Größe je Form (fm8-fix3).
export function symbolForm(k) { return String(k || '').split('-')[0]; }

// Sichtbarer Text (Kachel, aria-label, Warenkorb „Symbol"). Mit Farbvarianten (M1): „Herz weiß"/„Herz rot";
// ohne (M2): wie bisher „Herz"/„Pfote".
const SYMBOL_FARBWORT = { rot: 'rot', weiss: 'weiß' };
export function symbolText(sym, k) {
  if (k === 'keins') return 'ohne';                            // fm8-fix4 (M4): Symbol abgewählt
  const form = symbolForm(k) === 'pfote' ? 'Pfote' : 'Herz';
  if (!sym || !(sym.werte || []).some((w) => String(w).includes('-'))) return form;
  const z = String(k).split('-')[1];
  return form + ' ' + (z ? SYMBOL_FARBWORT[z] || z : 'weiß');
}

// fm8-fix4 (M4 Variante A): Zeichen-Boxen einer eingepassten Zeile wie getBoundingClientRect je Buchstabe
// (Referenz fm8-fix4-ref-m4v2.py, MEASURE_JS): Anfang/Ende je Zeichen (Vorschub inkl. Sperrung), Ober-/Unterkante der Zeile.
export function zeileAnker(M, r) {
  const e = r && r.elemente && r.elemente[0];
  if (!e) return null;
  const lauf = textLauf(M, e.font, e.size, e.text, e.ls);
  const x0 = e.align === 'center' ? e.x - lauf.breite / 2 : e.x;
  const starts = lauf.stueck.map((st) => x0 + st.x);
  const ends = starts.map((x, i) => (i < starts.length - 1 ? starts[i + 1] : x0 + lauf.breite));
  // Referenz: <span style="display:inline-block;line-height:1"> → Boxhöhe = Schriftgröße, Glyphen darin zentriert.
  const { a, d } = M.metrik(e.font);
  const h = e.size;
  return { text: e.text, starts, ends, links: x0, rechts: x0 + lauf.breite, top: e.y - a * e.size - (h - (a + d) * e.size) / 2, h };
}

function icon(kind, cx, cy, s, fill, rot) {
  // figures.icon: Herz s, Pfote s*0.92. rot (fm8-fix4, M4-Symbol 14°) nur, wenn gesetzt.
  if (kind === 'herz') return vektor('herz', cx, cy, 1, rot ? { s, hex: fill, rot } : { s, hex: fill });
  return vektor('pfote', cx, cy, 1, { s: s * 0.92, hex: fill, rot: rot || 0 });
}

// ---------- Szene ----------
// state: { farbe, groesse, felder:{id:text}, symbol, mitglieder:[...], menschen:[...], hunde:[...], rollen:[...] }
// Rückgabe: { elemente: [...] (Zeichenreihenfolge, Designpixel), fehler: [] }

export function baueSzene(cfg, state, M, F) {
  const el = [];
  // fm8-shop1 (D): Klickflächen je Mitglied (und Rotkehlchen) in Designpixeln — nur Bedienung, nie im Vertrag.
  const treffer = [];
  const box = (i, x0, y0, x1, y1, art = 'mitglied') => treffer.push({ art, i, x: x0, y: y0, w: x1 - x0, h: y1 - y0 });
  const nameBand = (n) => (n ? [n.y - n.h / 2, n.y + n.h / 2] : null);
  const T = (f) => { const r = passeText(M, f); el.push(...r.elemente); return r; };
  const feld = (id) => (cfg.felder || []).find((f) => f.id === id);
  const wert = (id) => {
    const f = feld(id);
    let v = state.felder && state.felder[id] != null ? state.felder[id] : (f && f.vorgabe) || '';
    if (istOhneText(state, f)) v = '';                           // Opt-out „Kein Text“: kein Text in Vorschau und Vertrag
    if (f && f.fest) v = f.text != null ? String(f.text) : (f.vorgabe || '');   // Textmatten (T3): feste Zeile, Zustand zählt nicht
    if (f && f.gross) v = v.toUpperCase();
    return v;
  };
  // Textmatten (T4): gewählte Textfarbe (cfg.textfarbe.werte[Wert]); Felder folgen ihr, außer textfarbe:false. Ohne cfg.textfarbe: null.
  const tf = cfg.textfarbe;
  const tfHex = tf ? (tf.werte || {})[state.textfarbe] || (tf.werte || {})[tf.vorgabe] || null : null;
  const feldHex = (f) => (tfHex && f.textfarbe !== false ? tfHex : f.farbe);
  const kopf = (id, extra = {}) => {
    const f = feld(id);
    if (!f) return;
    const text = wert(id);
    if (!text.trim()) return;
    const zeilen = text.split('\n').length;
    return T({
      id, text, font: f.font, px: zeilen > 1 && f.px2 ? f.px2 : f.px, ls: f.ls || 0,
      x: f.box[0], y: f.box[1], w: f.box[2], h: f.box[3], fitw: f.fitw, fith: f.fith, align: f.align, hex: feldHex(f), rotate: f.drehung, ...extra,
    });
  };
  // fm8-fix4 (M4 Variante A): Feld, dessen Box aus den Zeichen-Boxen einer anderen Zeile kommt (f.anker).
  // Wort = anker.wort: Box vom linken Rand des Zeichens anker.von bis zum rechten Rand von anker.bis, sonst die mittleren
  // anker.mitte der Wortbreite; fällt der Text unter anker.min × Startgröße, darf die Box auf die volle Wortbreite wachsen.
  const kopfAnker = (f, an) => {
    const text = wert(f.id);
    if (!text.trim()) return null;
    const fa = f.anker;
    const box = (l, r) => {
      const top = an.top + an.h * fa.oben, h = an.h * fa.hoehe;
      return { x: l + (r - l) / 2, y: top + h / 2, w: r - l, h, fitw: r - l, fith: h };
    };
    const probe = (b) => passeText(M, { id: f.id, text, font: f.font, px: f.px, ls: f.ls || 0, align: f.align, hex: f.farbe, rotate: f.drehung, ...b });
    let b;
    if (an.text.toUpperCase() === String(fa.wort).toUpperCase() && an.starts.length > fa.bis) b = box(an.starts[fa.von], an.ends[fa.bis]);
    else { const w = an.rechts - an.links; b = box(an.links + w * (1 - fa.mitte) / 2, an.links + w * (1 + fa.mitte) / 2); }
    let r = probe(b);
    if (r.px < f.px * fa.min) r = probe(box(an.links, an.rechts));
    el.push(...r.elemente);
    return r;
  };
  const A = cfg.anordnung;
  const mg = cfg.mitglieder || {};
  const gr = cfg.grafiken || {};
  const sym = cfg.symbol;
  const symbole = () => {
    if (!sym) return;
    const k = state.symbol || sym.vorgabe;
    const form = symbolForm(k);
    const farbe = typeof sym.farbe === 'string' ? sym.farbe : (sym.farbe || {})[k] || (sym.farbe || {})[form] || BK.TEXTFARBE;
    const gr2 = typeof sym.groesse === 'number' ? sym.groesse : (sym.groesse || {})[k] ?? (sym.groesse || {})[form];
    for (const [x, y] of sym.positionen) el.push(icon(form, x, y, gr2, farbe));
  };
  // Textmatten (T2): feste Grafiken als eigene Bild-Ebene (Designpixel, linke obere Ecke), damit die Druck-Gates (150 dpi,
  // 20-mm-Schutzzone) sie prüfen. b.farben: nur auf diesen Mattenfarben; b.hex bzw. b.textfarbe:true färbt ein (Datei mit Alpha).
  const festeBilder = (ebene) => {
    for (const b of cfg.bilder || []) {
      if ((b.ebene || 'unten') !== ebene || (b.farben && !b.farben.includes(state.farbe))) continue;
      const g = gr[b.key];
      if (!g || !g.url) continue;
      const hex = b.textfarbe && tfHex ? tfHex : b.hex;
      el.push({ typ: 'bild', key: b.key, url: g.url, vorschau: g.vorschau || g.url, x: b.x, y: b.y, w: b.w, h: b.h, ...(hex ? { hex } : {}) });
    }
  };
  if (cfg.bilder) festeBilder('unten');

  if (A === 'keine') {                                         // M1
    for (const f of cfg.felder) kopf(f.id);
    symbole();
  } else if (A === 'reihe-bild') {                             // M2
    for (const f of cfg.felder) kopf(f.id);
    symbole();
    const ms = state.mitglieder || [];
    if (ms.length) {
      const widths = ms.map((m) => gr[mg.typen[m.typ].grafik].slotbreite);
      const { xs, sc } = rowPositions(widths, mg.reihe_x[0], mg.reihe_x[1], mg.abstand, mg.skalierung_max || 1, mg.skalierung_min || 0.55);
      const namen = [];
      ms.forEach((m, i) => {
        const g = gr[mg.typen[m.typ].grafik];
        const b = bildAmPunkt(mg.typen[m.typ].grafik, g, xs[i], mg.grundlinie_y, sc);
        el.push(b);
        box(i, xs[i] - widths[i] * sc / 2, b.y, xs[i] + widths[i] * sc / 2, mg.name.mitte_y + mg.name.px * 0.8);
        if (m.name && m.name.trim()) namen.push(nameText(xs[i], mg.name.mitte_y, widths[i] * sc + (mg.name.zusatz || 0), m.name, mg.name.font, mg.name.px, { id: 'name' + (i + 1) }));
      });
      kappeNamen(namen).forEach((n) => T(n));
    }
  } else if (A === 'reihe-figur') {                            // M5
    for (const f of cfg.felder) kopf(f.id);
    const ms = state.mitglieder || [];
    if (ms.length) {
      const teile = ms.map((m) => {
        const t = mg.typen[m.typ];
        if (t.tier) {
          const [svg, w] = F.dogBack(m.fell || t.fell || 'golden', m.ohren || t.ohren || 'schlapp', m.groesse || t.groesse || 'gross', null, !!t.katze);
          return { svg, w, vek: { form: 'tier', fell: m.fell || t.fell || 'golden', ohren: m.ohren || t.ohren || 'schlapp', groesse: m.groesse || t.groesse || 'gross', katze: !!t.katze } };
        }
        const p = { kind: t.kind, haar: m.haar || t.haar || 'kurz', haarfarbe: m.haarfarbe || t.haarfarbe || 'braun',
          oberteil: m.oberteil || t.oberteil || '#c2453f', hose: m.hose || t.hose || '#4a6a9a', haut: m.haut || t.haut || 'hell', h: t.hoehe };
        const [svg, w] = F.personBack(p.kind, p.haar, p.haarfarbe, p.oberteil, p.hose, p.haut, p.h);
        return { svg, w, vek: { form: 'person', ...p } };
      });
      const { xs, sc } = rowPositions(teile.map((t) => t.w), mg.reihe_x[0], mg.reihe_x[1], mg.abstand, mg.skalierung_max || 1, mg.skalierung_min || 0.55);
      const rx = Math.trunc((xs[xs.length - 1] - xs[0]) / 2 + 120 * sc);
      el.push(vektor('ellipse', 0, 0, 1, { cx: 750, cy: mg.grundlinie_y + 2, rx, ry: 10, hex: '#000000', opacity: 0.25 }));
      teile.forEach((t, i) => {
        el.push(vektor(t.vek.form, xs[i], mg.grundlinie_y, sc, t.vek));
        const hoehe = t.vek.form === 'person' ? (t.vek.h || 400) : 330;
        box(i, xs[i] - t.w * sc / 2, mg.grundlinie_y - hoehe * sc, xs[i] + t.w * sc / 2, mg.name.mitte_y + mg.name.px * 0.8);
      });
      const namen5 = [];
      ms.forEach((m, i) => {
        if (m.name && m.name.trim()) namen5.push(nameText(xs[i], mg.name.mitte_y, Math.max(teile[i].w * sc, 120), m.name, mg.name.font, mg.name.px, { upper: true, ls: mg.name.ls, id: 'name' + (i + 1) }));
      });
      kappeNamen(namen5).forEach((n) => T(n));
    }
  } else if (A === 'fuesse') {                                 // M3
    for (const f of cfg.felder) kopf(f.id);
    for (const d of cfg.deko || []) el.push(vektor('pfad', 0, 0, 1, d));
    const ms = state.mitglieder || [];
    if (ms.length) {
      const widths = ms.map((m) => gr[mg.typen[m.typ].grafik].slotbreite);
      const { xs, sc } = rowPositions(widths, mg.reihe_x[0], mg.reihe_x[1], mg.abstand, mg.skalierung_max || 1, mg.skalierung_min || 0.55);
      const spur = (cfg.farben[state.farbe] || {}).spuren || '#8f8f8f';
      const pfeile = [], namen = [];
      ms.forEach((m, i) => {
        const t = mg.typen[m.typ];
        const by = t.tier ? mg.unterkante_y.tiere : mg.unterkante_y.menschen;
        const b = bildAmPunkt(t.grafik, gr[t.grafik], xs[i], by, sc, { hex: paarFarbe(mg, m, spur) });
        el.push(b);
        box(i, xs[i] - widths[i] * sc / 2, b.y, xs[i] + widths[i] * sc / 2, mg.name.mitte_y + mg.name.px * 0.8);
        const gerade = i % 2 === 0;
        const nx = xs[i] + (gerade ? 24 : -24) * sc;
        if (m.name && m.name.trim()) {
          namen.push(nameText(nx, mg.name.mitte_y, widths[i] * sc, m.name, mg.name.font, mg.name.px, { id: 'name' + (i + 1) }));
          pfeile.push(vektor('pfeil', 0, 0, 1, { x1: nx + (gerade ? -10 : 10), y1: mg.pfeil_y, x2: xs[i] + (gerade ? 6 : -6), y2: by + 22, bend: gerade ? 1 : -1, sw: 4.5, hex: BK.TEXTFARBE }));
        }
      });
      el.push(...pfeile);
      kappeNamen(namen).forEach((n) => T(n));
    }
  } else if (A === 'stiefel') {                                // M9 Gummistiefel (fm8-fix4)
    for (const f of cfg.felder) kopf(f.id);
    const ms = state.mitglieder || [];
    if (ms.length) {
      const typ = (m) => mg.typen[m.typ];
      const { xs, sc } = rowPositions(ms.map((m) => typ(m).breite * mg.skalierung), mg.reihe_x[0], mg.reihe_x[1], mg.abstand, 1, mg.skalierung_min || 0.5);
      const k = mg.skalierung * sc;
      const ty = mg.boden_y - BK_STIEFEL_BODEN * k;
      const namen = [];
      let hoechster = -1;
      ms.forEach((m, i) => {
        const t = typ(m);
        const farbe = m.farbe || t.farbe;
        const tx = xs[i] - (t.breite / 2) * k;
        if (t.art === 'stiefel') {
          const fs = mg.stiefel[farbe];
          el.push(vektor('stiefel', tx, ty, k, { modell: t.modell, muster: m.muster || t.muster, main: fs.main, pattern: fs.pattern, trim: fs.trim, id: '-m' + (i + 1) }));
          if (hoechster < 0 || t.oben < typ(ms[hoechster]).oben) hoechster = i;
        } else if (t.art === 'napf') el.push(vektor('napf', tx, ty, k, { main: mg.napf[farbe].main, accent: mg.napf[farbe].accent }));
        else el.push(vektor('pfotenspur', tx, ty, k, { main: mg.pfoten[farbe] }));
        if (m.name && m.name.trim()) namen.push(nameText(xs[i], mg.name.mitte_y, t.breite * k + mg.name.zusatz, m.name, mg.name.font, mg.name.px, { id: 'name' + (i + 1) }));
        box(i, tx, ty + (t.oben != null ? t.oben : 250) * k, tx + t.breite * k, mg.name.mitte_y + mg.name.px * 0.8);
      });
      const rk = cfg.rotkehlchen;
      // fm8-shop1 (H): Kunde wählt das Stiefelpaar (m.rotkehlchen am Mitglied); ohne Wahl wie bisher das höchste Paar
      const sitz = vogelPlatz(cfg, ms, hoechster);
      if (rk && state.rotkehlchen && sitz >= 0) {
        const t = typ(ms[sitz]);
        const kr = k * rk.skalierung;
        const tx = xs[sitz] - (t.breite / 2) * k;
        const vx = tx + t.linke_mitte * k - BK_ROTKEHLCHEN_FUSS[0] * kr, vy = ty + (t.oben - t.rand_ry * 0.4) * k - BK_ROTKEHLCHEN_FUSS[1] * kr;
        el.push(vektor('rotkehlchen', vx, vy, kr, {}));
        box(sitz, vx, vy, vx + 110 * kr, vy + 100 * kr, 'rotkehlchen');
      }
      kappeNamen(namen).forEach((n) => T(n));
    }
  } else if (A === 'herzen' && mg.stil === '3d') {           // M4 Variante A (fm8-fix4)
    let an = null;
    for (const f of cfg.felder) {
      if (f.anker && an) { kopfAnker(f, an); continue; }
      const r = kopf(f.id);
      if (f.id === 'zeile1') an = zeileAnker(M, r);
    }
    if (sym) {
      const k = state.symbol || sym.vorgabe;
      if (k !== 'keins' && an) {
        const form = symbolForm(k);
        const farbe = typeof sym.farbe === 'string' ? sym.farbe : (sym.farbe || {})[k] || BK.TEXTFARBE;
        const gr2 = typeof sym.groesse === 'number' ? sym.groesse : (sym.groesse || {})[k] ?? (sym.groesse || {})[form];
        const [dx, fy] = sym.anker.versatz;
        el.push(icon(form, an.rechts + dx + sym.anker.mitte, an.top + an.h * fy + sym.anker.mitte, gr2, farbe, sym.drehung));
      }
    }
    const ms = state.mitglieder || [];
    if (ms.length) {
      const { xs, sc } = rowPositions(ms.map(() => mg.slot), mg.reihe_x[0], mg.reihe_x[1], mg.abstand, mg.skalierung_max || 1, mg.skalierung_min || 0.55);
      const namen = [];
      ms.forEach((m, i) => {
        const j = i % mg.versatz.length;
        const farbe = m.farbe || mg.palette[i % mg.palette.length];
        el.push(vektor('herz3d', xs[i], mg.mitte_y + mg.versatz[j], 1,
          { art: mg.typen[m.typ].form, s: mg.groesse * sc * mg.skalen[j], hex: mg.palette_hex[farbe], rot: mg.drehungen[j] }));
        if (m.name && m.name.trim()) {
          namen.push({ x: Math.trunc(xs[i]), y: mg.name.mitte_y, w: Math.trunc(mg.slot * sc + mg.name.zusatz), h: mg.name.h, text: m.name,
            font: mg.name.font, px: mg.name.px, ls: mg.name.ls || 0, id: 'name' + (i + 1) });
        }
      });
      kappeNamen(namen).forEach((n) => T(n));
    }
  } else if (A === 'herzen') {                                 // M4
    for (const f of cfg.felder) kopf(f.id);
    const ms = state.mitglieder || [];
    if (ms.length) {
      const widths = ms.map(() => mg.slot);
      const { xs, sc } = rowPositions(widths, mg.reihe_x[0], mg.reihe_x[1], mg.abstand, mg.skalierung_max || 1, mg.skalierung_min || 0.55);
      const namen = [];
      ms.forEach((m, i) => {
        const form = mg.typen[m.typ].form;                     // 'herz' | 'pfote'
        const farbe = m.farbe || mg.palette[i % mg.palette.length];
        const key = 'm4_' + form + '_' + farbe;
        el.push(bildAmPunkt(key, gr[key], xs[i], mg.mitte_y, sc * (mg.groesse / 150)));
        if (m.name && m.name.trim()) namen.push(nameText(xs[i], mg.name.mitte_y, mg.slot * sc + 20, m.name, mg.name.font, mg.name.px, { id: 'name' + (i + 1) }));
      });
      kappeNamen(namen).forEach((n) => T(n));
    }
  } else if (A === 'pfoten-namen') {                           // M6
    kopf('zeile1');
    const menschen = (state.menschen || []).map((n) => n.trim()).filter(Boolean);
    // fm8-shop4 (B): jede Pfote wird gezeichnet, auch ohne Namen (dann nur die Pfote). Mit Namen unverändert.
    const hunde = (state.hunde || []).map((n) => String(n || '').trim()).slice(0, 3);
    const H = cfg.menschen;
    if (menschen.length) {
      const zl = namenZeilen(menschen, H.trenner, H.max_zeile);
      const ys = H.mitte_y[String(zl.length)];
      zl.forEach((z, i) => T({ id: 'menschen' + (i + 1), text: z + (i === zl.length - 1 && hunde.length ? H.endung : ''), font: H.font, px: H.px,
        x: H.box_x, y: ys[i], w: H.box_w, h: H.box_h, align: 'left', ls: 0 }));
    }
    if (hunde.length) {
      const pos = cfg.hunde.positionen[String(hunde.length)];
      const namen = [];
      hunde.forEach((name, i) => {
        const [x, y, s, r] = pos[i];
        el.push(vektor('pfote', x, y, 1, { s, hex: cfg.hunde.farbe || BK.TEXTFARBE, rot: r }));
        // designs.m6: Box int(x - s*.3), int(y + s*.07), int(s*.6) × int(s*.3), fit int(s*.52) × int(s*.3), Schrift int(s*.23), gedreht um r
        const left = Math.trunc(x - s * 0.3), top = Math.trunc(y + s * 0.07), w = Math.trunc(s * 0.6), h = Math.trunc(s * 0.3);
        if (name) namen.push({ id: 'hund' + (i + 1), text: name, font: cfg.hunde.font, px: Math.trunc(s * 0.23), ls: 0,
          x: left + Math.floor(w / 2), y: top + Math.floor(h / 2), w, h, fitw: Math.trunc(s * 0.52), fith: Math.trunc(s * 0.3),
          hex: cfg.hunde.textfarbe, rotate: r });
      });
      namen.forEach((n) => T(n));
    }
  } else if (A === 'namenzeile') {                             // M7
    kopf('zeile1');
    const H = cfg.menschen;
    const menschen = (state.menschen || []).map((n) => n.trim()).filter(Boolean);
    if (menschen.length) {
      const zl = namenZeilen(menschen, H.trenner, H.max_zeile);
      const ys = H.mitte_y[String(zl.length)];
      zl.forEach((z, i) => T({ id: 'namen' + (i + 1), text: z.toUpperCase(), font: H.font, px: H.px, ls: H.ls,
        x: 750, y: ys[i], w: H.box_w, h: H.box_h }));
    }
  } else if (A === 'rollenliste') {                            // M8
    kopf('zeile1'); kopf('zeile2');
    for (const d of cfg.deko || []) {
      if (d.form === 'pfote') {
        // anker: Pfote rechts am tatsächlichen Wortende (Referenz „RUDEL“: Ende 1063 + 25 = 1088 = Paketwert)
        let x = d.x;
        if (d.anker) {
          const z = el.filter((e) => e.typ === 'text' && (e.id === d.anker.feld || e.id.startsWith(d.anker.feld + '_')));
          if (z.length) x = Math.max(...z.map((e) => e.x + textLauf(M, e.font, e.size, e.text, e.ls).breite / 2)) + d.anker.dx;
        }
        el.push(vektor('pfote', x, d.y, 1, { s: d.s, hex: d.hex, rot: d.rot || 0 }));
      } else el.push(vektor('pfad', 0, 0, 1, d));
    }
    const R = cfg.rollen;
    const rows = (state.rollen || []).filter((r) => (r.rolle || '').trim() || (r.name || '').trim()).slice(0, R.max);
    const n = rows.length;
    rows.forEach((r, i) => {
      const row = Math.floor(i / 2), c = i % 2;
      const x = (i === n - 1 && n % 2 === 1) ? 750 : R.spalten_x[c];
      const y = R.zeile_y0 + row * R.zeile_abstand;
      if ((r.rolle || '').trim()) T({ id: 'rolle' + (i + 1), text: r.rolle.toUpperCase(), font: R.rolle.font, px: R.rolle.px, ls: R.rolle.ls,
        x, y: y - 40, w: 520, h: 50, hex: R.rolle.farbe });
      if ((r.name || '').trim()) T({ id: 'rname' + (i + 1), text: r.name, font: R.name.font, px: R.name.px, ls: 0, x, y: y + 22, w: 520, h: 80 });
    });
  }
  if (cfg.bilder) festeBilder('oben');
  // fm8-fix5: helle Mattenfarben (farben[x].text): alles bisher Weiße in der Schriftfarbe, dazu die Ersatzfarben der Matte
  // (cfg.text_ersatz {altHex: neuHex | 'text'}, z. B. M6 Hundenamen in den Pfoten hell). Ohne text: unverändert.
  const fa = (cfg.farben || {})[state.farbe] || {};
  if (fa.text) faerbeUm(el, fa.text, cfg.text_ersatz || {});
  // fm8-fix5: Schatten (Rasen) unter Texten und weißen Elementen, auch weiße Grafiken ohne Einfärbung (M2-Kreidefiguren)
  if (fa.schatten) for (const e of el) if (e.typ === 'text' || String(e.hex || '').toLowerCase() === BK.TEXTFARBE || (e.typ === 'bild' && !e.hex)) e.schatten = true;
  return { elemente: el, treffer };
}

// fm8-shop1 (F, M3): Farbe je Fuß-/Pfotenpaar. mg.paarfarben {key: hex|null}; null bzw. ohne Wahl = Spurfarbe der Matte (Grau, wie bisher).
export function paarFarbe(mg, m, spur) {
  const p = (mg && mg.paarfarben) || null;
  if (!p || !m || !m.farbe || !p[m.farbe]) return spur;
  return p[m.farbe];
}

// fm8-shop1 (H, M9): Index des Paars mit Rotkehlchen — gewähltes Stiefelpaar (m.rotkehlchen), sonst das höchste (hoechster).
export function vogelPlatz(cfg, ms, hoechster) {
  const mg = cfg.mitglieder || {};
  const i = (ms || []).findIndex((m) => m && m.rotkehlchen && mg.typen && mg.typen[m.typ] && mg.typen[m.typ].art === 'stiefel');
  return i >= 0 ? i : hoechster;
}

// fm8-shop1 (D/E): Typ eines Mitglieds tauschen. Name bleibt; Aussehen-Werte bleiben nur, wenn der neue Typ sie anbietet;
// die Paarfarbe (M3) bleibt; das Rotkehlchen (M9) nur auf einem Stiefelpaar.
export function typWechsel(cfg, m, neuTyp) {
  const mg = cfg.mitglieder || {};
  const t = (mg.typen || {})[neuTyp];
  if (!t) return m;
  const n = { typ: neuTyp, name: m.name || '' };
  const o = t.optionen || {};
  for (const [k, v] of Object.entries(m)) {
    if (k === 'typ' || k === 'name') continue;
    if (k === 'farbe' && mg.paarfarben && mg.paarfarben[v] !== undefined) { n.farbe = v; continue; }
    if (k === 'rotkehlchen') { if (v && t.art === 'stiefel') n.rotkehlchen = true; continue; }
    if (Array.isArray(o[k]) && o[k].includes(v)) n[k] = v;
  }
  return n;
}

// fm8-shop3 (A): Typ für ein neues Mitglied an Platz i — Reihenfolge Mann, Frau, Kind, Kind, Hund, dann Baby/Katze/…,
// je Matte der erste vorhandene Typ der Liste (Typ-Schlüssel der Matte), sonst der erste Typ.
const BK_VORGABE_PLAETZE = [['mann', 'herren', 'erwachsen', 'mensch'], ['frau', 'damen', 'erwachsen', 'mensch'], ['kind', 'junge', 'maedchen', 'jugend', 'mensch'],
  ['kind', 'maedchen', 'kleinkind', 'junge', 'mensch'], ['hund', 'napf', 'pfoten'], ['baby', 'kleinkind', 'katze', 'hund_klein', 'mensch'],
  ['katze', 'hund_klein', 'baby', 'pfoten', 'mensch'], ['oma', 'opa', 'jugend', 'kind', 'mensch']];
export function vorgabeTyp(cfg, i) {
  const typen = (cfg.mitglieder && cfg.mitglieder.typen) || {};
  const platz = BK_VORGABE_PLAETZE[Math.min(i, BK_VORGABE_PLAETZE.length - 1)];
  return platz.find((k) => typen[k]) || Object.keys(typen)[0];
}
// fm8-shop4 (A): nächster freier Beispielname (nie doppelt, nie schon vergeben — Groß/klein egal)
export const BK_BEISPIEL = {
  person: ['Anna', 'Max', 'Lena', 'Paul', 'Mia', 'Tom', 'Emma', 'Ben', 'Lisa', 'Finn'],
  tier: ['Bello', 'Luna', 'Mimi', 'Frieda', 'Rocky', 'Nala', 'Kira', 'Sammy'],
  rolle: ['Sofa-Wächterin', 'Leckerli-Prüfer', 'Chef-Kuschler', 'Ballwerferin', 'Gassi-Profi', 'Kuschelchef', 'Napfkontrolle', 'Türsteher'],
};
export function beispielName(liste, vergeben) {
  const v = new Set([...(vergeben || [])].map((x) => String(x || '').trim().toLowerCase()).filter(Boolean));
  return (liste || []).find((n) => !v.has(n.toLowerCase())) || '';
}

// fm8-shop3 (B): Person oder Haustier (Blocküberschrift „1. Person“ / „4. Haustier“)
export function istHaustier(cfg, typ) {
  const t = ((cfg.mitglieder || {}).typen || {})[typ] || {};
  return !!(t.tier || t.form === 'pfote' || t.art === 'napf' || t.art === 'pfotenspur' || /^(hund|katze|napf|pfoten|hund_klein)$/.test(typ));
}

// fm8-shop1 (D): Treffer an der Bühne (Designpixel) → { art, i } oder null. Rotkehlchen vor Mitglied, sonst nächste Mitte.
export function trefferAn(treffer, x, y, rand = 12) {
  const drin = (t) => x >= t.x - rand && x <= t.x + t.w + rand && y >= t.y - rand && y <= t.y + t.h + rand;
  const liste = (treffer || []).filter(drin);
  if (!liste.length) return null;
  const v = liste.find((t) => t.art === 'rotkehlchen');
  if (v) return { art: v.art, i: v.i };
  liste.sort((a, b) => Math.abs(a.x + a.w / 2 - x) - Math.abs(b.x + b.w / 2 - x));
  return { art: liste[0].art, i: liste[0].i };
}

export function faerbeUm(elemente, text, ersatz) {
  const neu = (hex) => {
    const h = String(hex || '').toLowerCase();
    if (h === BK.TEXTFARBE) return text;
    const r = ersatz[h];
    return r === 'text' ? text : r || hex;
  };
  for (const e of elemente) if (e.hex) e.hex = neu(e.hex);
}

// ---------- Druckvertrag (_ttg_baukasten_config v1, mm im 600×400-Raum) ----------
const r2 = (v) => Math.round(v * 100) / 100;
const r4 = (v) => Math.round(v * 10000) / 10000;

export function bauVertrag(cfg, state, szene) {
  const mm = BK.MM;
  const fa = cfg.farben[state.farbe] || {};
  const hintergrund = { hex: fa.druck };
  if (fa.druckbild) hintergrund.url = fa.druckbild;
  const elemente = szene.elemente.map((e) => {
    if (e.typ === 'bild') {
      const o = { typ: 'bild', key: e.key, url: e.url, x: r2(e.x * mm), y: r2(e.y * mm), w: r2(e.w * mm), h: r2(e.h * mm) };
      if (e.hex) o.hex = e.hex;
      if (e.schatten) o.schatten = true;
      return o;
    }
    if (e.typ === 'vektor') {
      const { typ, form, tx, ty, k, ...p } = e;
      return { typ: 'vektor', form, tx: r2(tx * mm), ty: r2(ty * mm), k: r4(k * mm), ...p };
    }
    const o = { typ: 'text', id: e.id, text: e.text, font: e.font, size: r2(e.size * mm), x: r2(e.x * mm), y: r2(e.y * mm),
      align: e.align, ls: e.ls || 0, hex: e.hex, maxW: r2(e.maxW * mm) };
    if (e.rotate) { o.rotate = e.rotate; o.rx = r2(e.rx * mm); o.ry = r2(e.ry * mm); }
    if (e.schatten) o.schatten = true;
    return o;
  });
  const v = { v: 1, art: 'baukasten', design: cfg.design, size: state.groesse, farbe: state.farbe, hintergrund, elemente };
  if (elemente.some((e) => e.schatten)) {
    const S = BK.SCHATTEN;
    v.schatten = { dx: r2(S.dx * mm), dy: r2(S.dy * mm), sigma: r2(S.sigma * mm), alpha: S.alpha };   // fm8-fix5, mm wie der Rest
  }
  return v;
}

// Sichtbare Warenkorb-Eigenschaften + Vertrag. Leere Werte fallen weg (wie ttg-family.js).
// opt (Textmatten T1/T4): { farbeAlsEigenschaft, textfarbeAlsEigenschaft } — wenn das Produkt die Achse nicht als Option führt.
export function bauEigenschaften(cfg, state, vertrag, opt = {}) {
  const p = {};
  for (const f of cfg.felder || []) {
    if (f.fest) continue;                                     // Textmatten (T3): feste Zeile ist keine Kundenangabe
    const v = (state.felder && state.felder[f.id] != null ? state.felder[f.id] : f.vorgabe || '').trim();
    // Opt-out wie die Engine (getProperties): kein Text am Ende → benannt „Kein Text“ statt weglassen
    if (hatOptOut(f) && (istOhneText(state, f) || !v)) { p[f.label] = BK_KEIN_TEXT; continue; }
    if (v) p[f.label] = v.replace(/\n/g, ' / ');
  }
  if (opt.farbeAlsEigenschaft && state.farbe) p[cfg.farbe_label || 'Mattenfarbe'] = state.farbe;
  if (opt.textfarbeAlsEigenschaft && cfg.textfarbe && state.textfarbe) p[cfg.textfarbe.label || cfg.textfarbe.option || 'Schriftfarbe'] = state.textfarbe;
  if (cfg.symbol) p['Symbol'] = symbolText(cfg.symbol, state.symbol || cfg.symbol.vorgabe);
  const mg = cfg.mitglieder;
  if (mg && Array.isArray(state.mitglieder) && state.mitglieder.length) {
    const details = (m) => {
      // fm8-shop1 (F): M3-Paarfarbe nur, wenn gewählt und nicht Grau (Vorgabe) — sonst unverändert
      if (mg.paarfarben && m.farbe && mg.paarfarben[m.farbe]) return ' (' + ((mg.paarfarbnamen || {})[m.farbe] || m.farbe) + ')';
      if (!mg.details_in_eigenschaft || !mg.typen[m.typ]) return '';
      const t = mg.typen[m.typ];
      const fn = (mg.farbnamen || {})[m.farbe || t.farbe] || m.farbe || t.farbe;
      return ' (' + fn + (t.art === 'stiefel' ? ', ' + ((mg.wertnamen || {})[m.muster || t.muster] || m.muster || t.muster) : '') + ')';
    };
    p[mg.eigenschaft || 'Familie'] = state.mitglieder
      .map((m) => (mg.typen[m.typ] ? mg.typen[m.typ].label : m.typ) + details(m) + (m.name && m.name.trim() ? ' ' + m.name.trim() : '')).join(' · ');
  }
  if (cfg.rotkehlchen) {
    // fm8-shop1 (H): gewähltes Paar nennen; ohne Wahl wie bisher nur „ja“
    const ms = state.mitglieder || [];
    const w = ms.findIndex((m) => m && m.rotkehlchen && mg && mg.typen[m.typ] && mg.typen[m.typ].art === 'stiefel');
    const wer = w >= 0 ? ((ms[w].name || '').trim() || mg.typen[ms[w].typ].label) : '';
    p[cfg.rotkehlchen.eigenschaft || 'Rotkehlchen'] = state.rotkehlchen ? (wer ? 'ja, auf ' + wer : 'ja') : 'nein';
  }
  const menschen = (state.menschen || []).map((n) => n.trim()).filter(Boolean);
  if (cfg.menschen && menschen.length) p[cfg.menschen.eigenschaft || 'Namen'] = menschen.join(' · ');
  const hunde = (state.hunde || []).map((n) => n.trim()).filter(Boolean);
  if (cfg.hunde && hunde.length) p['Hunde'] = hunde.join(' · ');
  const rollen = (state.rollen || []).filter((r) => (r.name || '').trim() || (r.rolle || '').trim());
  if (cfg.rollen && rollen.length) p['Rollen'] = rollen.map((r) => ((r.rolle || '').trim() ? r.rolle.trim() + ': ' : '') + (r.name || '').trim()).join(' · ');
  p['_ttg_baukasten_config'] = JSON.stringify(vertrag);
  return p;
}

// Kaufbarkeits-Gate: Pflichtfelder, Zeichenregeln, Mindestzahl Mitglieder. Meldet, deaktiviert nie.
export function pruefeZustand(cfg, state) {
  const fehler = [];
  for (const f of cfg.felder || []) {
    if (f.fest) continue;                                     // Textmatten (T3): feste Zeile wird nicht eingegeben
    if (istOhneText(state, f)) continue;                      // Opt-out „Kein Text“: Feld zählt nicht (Engine _textFilled)
    const v = state.felder && state.felder[f.id] != null ? state.felder[f.id] : (f.vorgabe || '');
    const p = pruefeEingabe(v, { max: f.max, zeilen: f.zeilen || 1, fehlen: fehlendeZeichen(cfg, f.font) });
    if (p.fehler) fehler.push({ feld: f.id, text: f.label + ': ' + p.fehler });
    else if (f.pflicht && !v.trim()) fehler.push({ feld: f.id, text: 'Bitte ' + f.label + ' ausfüllen.' });
  }
  const namenRegel = (liste, max, wo, font) => liste.forEach((n, i) => {
    const p = pruefeEingabe(n, { max, zeilen: 1, fehlen: fehlendeZeichen(cfg, font) });
    if (p.fehler) fehler.push({ feld: wo + i, text: p.fehler });
  });
  const mg = cfg.mitglieder;
  if (mg) {
    if (!(state.mitglieder || []).length) fehler.push({ feld: 'mitglieder', text: 'Bitte mindestens ein Familienmitglied hinzufügen.' });
    namenRegel((state.mitglieder || []).map((m) => m.name || ''), cfg.regeln.namen_max, 'mitglied', mg.name && mg.name.font);
  }
  if (cfg.menschen) {
    const menschen = (state.menschen || []).filter((n) => n.trim());
    if (!menschen.length) fehler.push({ feld: 'menschen', text: 'Bitte mindestens einen Namen eintragen.' });
    namenRegel(state.menschen || [], cfg.regeln.namen_max, 'mensch', cfg.menschen.font);
  }
  if (cfg.hunde) {
    const hunde = (state.hunde || []).filter((n) => n.trim());
    if (hunde.length < (cfg.hunde.min || 1)) fehler.push({ feld: 'hunde', text: 'Bitte mindestens einen Hundenamen eintragen.' });
    namenRegel(state.hunde || [], cfg.regeln.namen_max, 'hund', cfg.hunde.font);
  }
  if (cfg.rollen) {
    const rollen = (state.rollen || []).filter((r) => (r.name || '').trim());
    if (!rollen.length) fehler.push({ feld: 'rollen', text: 'Bitte mindestens einen Namen eintragen.' });
    (state.rollen || []).forEach((r, i) => {
      const a = pruefeEingabe(r.rolle || '', { max: cfg.regeln.rolle_max, zeilen: 1, fehlen: fehlendeZeichen(cfg, cfg.rollen.rolle && cfg.rollen.rolle.font) });
      const b = pruefeEingabe(r.name || '', { max: cfg.regeln.namen_max, zeilen: 1, fehlen: fehlendeZeichen(cfg, cfg.rollen.name && cfg.rollen.name.font) });
      if (a.fehler) fehler.push({ feld: 'rolle' + i, text: a.fehler });
      if (b.fehler) fehler.push({ feld: 'rname' + i, text: b.fehler });
    });
  }
  return { ok: fehler.length === 0, fehler };
}

// Startzustand aus dem Beispiel des Metafelds (Vorschau beim Laden komplett, UX-Pflicht).
export function startZustand(cfg, variante) {
  const b = cfg.beispiel || {};
  const st = {
    farbe: (variante && variante.farbe) || Object.keys(cfg.farben)[0],
    groesse: (variante && variante.groesse) || '40x60',
    felder: { ...(b.felder || {}) },
    symbol: b.symbol || (cfg.symbol ? cfg.symbol.vorgabe : null),
    mitglieder: (b.mitglieder || []).map((m) => ({ ...m })),
    menschen: [...(b.menschen || [])],
    hunde: [...(b.hunde || [])],
    rollen: (b.rollen || []).map((r) => ({ ...r })),
  };
  for (const f of cfg.felder || []) if (st.felder[f.id] == null) st.felder[f.id] = f.vorgabe || '';
  const optOut = (cfg.felder || []).filter(hatOptOut);           // Opt-out: Startzustand = defaultNoText (nur mit Schlüssel im Zustand)
  if (optOut.length) st.ohneText = Object.fromEntries(optOut.map((f) => [f.id, f.defaultNoText]));
  if (cfg.rotkehlchen) st.rotkehlchen = b.rotkehlchen != null ? !!b.rotkehlchen : !!cfg.rotkehlchen.vorgabe;   // M9
  if (cfg.textfarbe) {                                        // Textmatten (T4): Wert aus der Variante, sonst Beispiel/Vorgabe
    const w = cfg.textfarbe.werte || {};
    const ausVariante = variante && variante.optionen ? variante.optionen[cfg.textfarbe.option] : null;
    st.textfarbe = [ausVariante, b.textfarbe, cfg.textfarbe.vorgabe].find((x) => x && w[x]) || Object.keys(w)[0];
  }
  return st;
}

// ---------- Varianten (Größe × Farbe), order-agnostisch ----------
export function groessenSchluessel(wert) {
  const m = String(wert).match(/(\d+)\D+?(\d+)/);
  return m ? m[1] + 'x' + m[2] : null;
}

// Textmatten (T1/T4): Größe ist Pflichtachse. „Farbe“ und weitere Achsen ({Optionsname: Wert}, z. B. Schriftfarbe) zählen nur,
// wenn das Produkt die Option führt — sonst wählt der Baukasten sie selbst (Eigenschaft statt Variante).
export function hatOption(product, name) {
  return !!(product && Array.isArray(product.options) && product.options.some((o) => o.name === name));
}

export function findeVariante(product, groesse, farbe, opt = BK_OPTION, weitere = null) {
  if (!product || !Array.isArray(product.options)) return null;
  const gi = product.options.findIndex((o) => o.name === opt.groesse);
  const fi = product.options.findIndex((o) => o.name === opt.farbe);
  if (gi < 0) return null;
  const achsen = Object.entries(weitere || {}).map(([n, w]) => [product.options.findIndex((o) => o.name === n), w]).filter(([i]) => i >= 0);
  const v = product.variants.find((it) => groessenSchluessel(it.options[gi]) === groesse && (fi < 0 || it.options[fi] === farbe)
    && achsen.every(([i, w]) => it.options[i] === w));
  return v ? { id: v.id, available: v.available !== false, price: v.price, compareAtPrice: v.compareAtPrice, groesseWert: v.options[gi] } : null;
}

export function varianteZuZustand(product, id, opt = BK_OPTION, weitere = []) {
  if (!product) return null;
  const gi = product.options.findIndex((o) => o.name === opt.groesse);
  const fi = product.options.findIndex((o) => o.name === opt.farbe);
  const v = product.variants.find((it) => String(it.id) === String(id));
  if (!v || gi < 0) return null;
  const z = { groesse: groessenSchluessel(v.options[gi]), farbe: fi < 0 ? null : v.options[fi] };
  const optionen = {};
  for (const n of weitere || []) { const i = product.options.findIndex((o) => o.name === n); if (i >= 0) optionen[n] = v.options[i]; }
  if (Object.keys(optionen).length) z.optionen = optionen;
  return z;
}

export function formatEUR(cents) {
  if (cents == null || isNaN(cents)) return '';
  return (cents / 100).toFixed(2).replace('.', ',') + ' €';
}

export function cdnBreite(url, w) {
  if (!url) return url;
  return url + (url.includes('?') ? '&' : '?') + 'width=' + w;
}

export function neueGruppe(now = Date.now(), rand = Math.random()) {
  return 'g' + now.toString(36) + Math.floor(rand * 1e6).toString(36);
}

export function vorschauAuftrag(group, dataUrl, pu) {
  if (!pu || !pu.enabled || !pu.url || !group || !dataUrl) return null;
  return { group, dataUrl, url: pu.url, property: pu.property || '_preview_image', maxUploadBytes: pu.maxUploadBytes || 512000 };
}

// ---------- Zeichnen (Canvas, Designpixel) ----------
// Für Browser UND Messharness: zeichnet eine Szene auf ctx (Transform: 1 Einheit = 1 Designpixel).
// bilder: { url -> HTMLImageElement|Canvas (bereits eingefärbt, falls hex) }, vektorBild(run) -> Image
export function svgDokument(laeufe, F) {
  // Feste Größe 1500×1000 (= viewBox): dasselbe Bild dient Bühne und Warenkorbbild (Cache), der Browser
  // rastert SVG-Bilder beim drawImage in der Zielgröße.
  const w = BK.W, h = BK.H;
  let defs = '';
  let inhalt = '';
  for (const e of laeufe) {
    const [frag, d] = vektorFragment(e, F);
    if (d && !defs.includes(d)) defs += d;
    inhalt += '<g transform="translate(' + e.tx + ' ' + e.ty + ') scale(' + e.k + ')">' + frag + '</g>';
  }
  return '<svg xmlns="http://www.w3.org/2000/svg" width="' + w + '" height="' + h + '" viewBox="0 0 ' + BK.W + ' ' + BK.H + '"><defs>' + defs + '</defs>' + inhalt + '</svg>';
}

export function vektorFragment(e, F) {
  switch (e.form) {
    case 'herz': return [e.rot ? '<g transform="rotate(' + e.rot + ')">' + F.heart(0, 0, e.s, e.hex) + '</g>' : F.heart(0, 0, e.s, e.hex), ''];
    case 'herz3d': return F.heart3d(e.s, e.hex, e.rot || 0, e.art);
    case 'stiefel': { const t = F.S.teile(e.modell, { pattern: e.muster, colors: { main: e.main, pattern: e.pattern, trim: e.trim }, id: e.id }); return [t.body, t.defs]; }
    case 'napf': { const t = F.S.teile('napf', { colors: { main: e.main, accent: e.accent } }); return [t.body, t.defs]; }
    case 'pfotenspur': { const t = F.S.teile('pfoten', { colors: { main: e.main } }); return [t.body, t.defs]; }
    case 'rotkehlchen': { const t = F.S.teile('rotkehlchen', {}); return [t.body, t.defs]; }
    case 'pfote': return [F.paw(0, 0, e.s, e.hex, e.rot || 0), ''];
    case 'pfeil': return [F.arrow(e.x1, e.y1, e.x2, e.y2, e.bend, e.sw, e.hex), ''];
    case 'pfad': return ['<path d="' + e.d + '" stroke="' + e.hex + '" stroke-width="' + e.sw + '" fill="none" stroke-linecap="round" stroke-linejoin="round" opacity="' + (e.opacity != null ? e.opacity : 1) + '"/>', ''];
    case 'ellipse': return ['<ellipse cx="' + e.cx + '" cy="' + e.cy + '" rx="' + e.rx + '" ry="' + e.ry + '" fill="' + e.hex + '" opacity="' + (e.opacity != null ? e.opacity : 1) + '"/>', ''];
    case 'person': return [F.personBack(e.kind, e.haar, e.haarfarbe, e.oberteil, e.hose, e.haut, e.h)[0], F.SHADE];
    case 'tier': return [F.dogBack(e.fell, e.ohren, e.groesse, null, !!e.katze)[0], F.SHADE];
    default: return ['', ''];
  }
}

// Läufe aufeinanderfolgender Vektoren bündeln (ein SVG-Bild je Lauf, Reihenfolge bleibt)
export function zeichenSchritte(szene) {
  const out = [];
  for (const e of szene.elemente) {
    if (e.typ === 'vektor') {
      const last = out[out.length - 1];
      if (last && last.typ === 'vektoren' && !!last.schatten === !!e.schatten) last.liste.push(e);
      else out.push({ typ: 'vektoren', liste: [e], ...(e.schatten ? { schatten: true } : {}) });
    } else out.push(e);
  }
  return out;
}

export function zeichneText(ctx, e, familie) {
  const lauf = e._lauf;
  ctx.save();
  if (e.rotate) { ctx.translate(e.rx, e.ry); ctx.rotate(e.rotate * Math.PI / 180); ctx.translate(-e.rx, -e.ry); }
  ctx.fillStyle = e.hex;
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  ctx.font = e.size + 'px "' + familie(e.font) + '"';
  const x0 = e.align === 'center' ? e.x - lauf.breite / 2 : e.x;
  for (const s of lauf.stueck) {
    if (s.art === 'emoji') {
      const g = e.size * 0.95;
      const p = typeof Path2D !== 'undefined' ? new Path2D(BK_EMOJI[s.wert]) : null;
      if (p) {
        ctx.save();
        ctx.translate(x0 + s.x, e.y - e.size * 0.35 - g / 2);
        ctx.scale(g / 24, g / 24);
        ctx.fill(p);
        ctx.restore();
      }
    } else ctx.fillText(s.wert, x0 + s.x, e.y);
  }
  ctx.restore();
}

// Ganze Szene zeichnen (Shop UND Messharness benutzen genau diese Funktion).
// opt: { F, buehne (Bild|null), grundfarbe (hex), bildFuer(e) -> Zeichenquelle|null,
//        svgFuer(liste) -> Promise<Bild|null>, familie(key) -> CSS-Familienname }
export async function zeichneSzene(ctx, sz, pxW, pxH, opt) {
  const schritte = zeichenSchritte(sz);
  const svgs = await Promise.all(schritte.map((s) => (s.typ === 'vektoren' ? opt.svgFuer(s.liste) : null)));
  ctx.save();
  ctx.scale(pxW / BK.W, pxH / BK.H);
  if (opt.buehne) ctx.drawImage(opt.buehne, 0, 0, BK.W, BK.H);
  else { ctx.fillStyle = opt.grundfarbe || '#161616'; ctx.fillRect(0, 0, BK.W, BK.H); }
  // fm8-fix5: Canvas-Schatten wirkt in Gerätepixeln (ohne Transform) → mit dem Maßstab umrechnen
  const k = pxW / BK.W, S = BK.SCHATTEN;
  const schatten = (an) => {
    ctx.shadowColor = an ? 'rgba(0,0,0,' + S.alpha + ')' : 'rgba(0,0,0,0)';
    ctx.shadowBlur = an ? 2 * S.sigma * k : 0; ctx.shadowOffsetX = an ? S.dx * k : 0; ctx.shadowOffsetY = an ? S.dy * k : 0;
  };
  schritte.forEach((s, i) => {
    schatten(!!s.schatten);
    if (s.typ === 'vektoren') { if (svgs[i]) ctx.drawImage(svgs[i], 0, 0, BK.W, BK.H); return; }
    if (s.typ === 'bild') { const q = opt.bildFuer(s); if (q) ctx.drawImage(q, s.x, s.y, s.w, s.h); return; }
    if (!s._lauf) throw new Error('Text ohne Lauf: ' + s.id);
    zeichneText(ctx, s, opt.familie);
  });
  ctx.restore();
}

// Einfärben (M3-Spuren): Quelle source-in mit der Farbe — wie ttg-fuesse.js / render.einfaerben.
export function toenen(src, hex) {
  const c = document.createElement('canvas');
  c.width = src.naturalWidth || src.width; c.height = src.naturalHeight || src.height;
  const x = c.getContext('2d');
  x.drawImage(src, 0, 0);
  x.globalCompositeOperation = 'source-in';
  x.fillStyle = hex;
  x.fillRect(0, 0, c.width, c.height);
  return c;
}

export function svgDatenUrl(svg) { return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg); }

// ---------- Custom Element (nur Browser) ----------

function defineBaukasten() {
  const FORM_SELECTOR = 'product-form-component form[data-type="add-to-cart-form"]';
  const el = (tag, cls, text) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  };
  const ladeBild = (url, prio = 'auto') => new Promise((res, rej) => {
    const i = new Image();
    i.crossOrigin = 'anonymous';
    if (prio !== 'auto') i.fetchPriority = prio;
    i.decoding = 'async';
    i.onload = () => res(i);
    i.onerror = () => rej(new Error('Bild nicht ladbar: ' + url));
    i.src = url;
  });
  const familie = (key) => 'TTG-BK-' + key;

  class TTGBaukasten extends HTMLElement {
    connectedCallback() {
      if (this._gestartet) { if (this._bereit) document.body.classList.add('ttg-baukasten-active'); return; }
      this._gestartet = true;
      this._bilder = {};
      this._svgCache = new Map();
      this.init().catch((e) => {
        console.error('[ttg-baukasten] Start fehlgeschlagen:', e);
        this.setAttribute('data-ttg-failed', 'init');
        this._notiz(this._s().load_error || 'Vorschau konnte nicht geladen werden. Bitte Seite neu laden.');
      });
    }

    disconnectedCallback() { document.body.classList.remove('ttg-baukasten-active'); }

    _cfgEl() {
      if (!this._c) {
        const n = this.querySelector('script[type="application/json"][data-ttg-baukasten-config]');
        this._c = n ? JSON.parse(n.textContent) : {};
      }
      return this._c;
    }

    _s() { return (this._cfgEl().strings) || {}; }
    _notiz(t) { const n = this.querySelector('[data-bk-note]'); if (n) n.textContent = t; }
    _form() { return document.querySelector(FORM_SELECTOR) || document.querySelector('product-form-component form'); }

    async init() {
      const c = this._cfgEl();
      this.cfg = c.baukasten;
      if (!this.cfg || this.cfg.version !== 1) throw new Error('matten_baukasten fehlt oder ist nicht version 1');
      this.product = c.product || null;
      this.previewUpload = c.previewUpload || null;
      this.F = await import(c.figurenSrc);
      if (this.cfg.anordnung === 'stiefel') this.F = { ...this.F, S: await import(c.stiefelSrc) };   // M9 (fm8-fix4)
      const v0 = varianteZuZustand(this.product, this.product && this.product.initialVariantId, BK_OPTION, this.cfg.textfarbe ? [this.cfg.textfarbe.option] : []);
      this.state = startZustand(this.cfg, v0);
      this._canvas = this.querySelector('canvas[data-bk-preview]');
      this._mctx = document.createElement('canvas').getContext('2d');
      await Promise.all([this._ladeSchriften(), this._ladeBuehne(this.state.farbe, 'high'), this._ladeGrafiken()]);
      this._metrik = {};
      this.M = {
        breite: (f, px, s) => { this._mctx.font = px + 'px "' + familie(f) + '"'; return this._mctx.measureText(s).width; },
        tinte: (f, px, s) => { this._mctx.font = px + 'px "' + familie(f) + '"'; const m = this._mctx.measureText(s); return { l: m.actualBoundingBoxLeft || 0, r: m.actualBoundingBoxRight || 0 }; },
        metrik: (f) => {
          if (!this._metrik[f]) {
            this._mctx.font = '1000px "' + familie(f) + '"';
            const m = this._mctx.measureText('Hxg');
            this._metrik[f] = { a: m.fontBoundingBoxAscent / 1000, d: m.fontBoundingBoxDescent / 1000 };
          }
          return this._metrik[f];
        },
      };
      this._baueUI();
      this._scharfSchalten();
      this._galerie();
      this._buehneKlick();
      this._infoBloecke();
      this._mini();
      window.addEventListener('resize', () => this._planeZeichnen());
      this._aenderung();
      this._notiz('');
      this._bereit = true;
      document.body.classList.add('ttg-baukasten-active');
      this._kopfNachOben();
    }

    async _ladeSchriften() {
      if (typeof FontFace === 'undefined') return;
      const jobs = Object.entries(this.cfg.schriften || {}).map(async ([key, url]) => {
        const face = new FontFace(familie(key), 'url(' + url + ')');
        await face.load();
        document.fonts.add(face);
      });
      await Promise.all(jobs);
    }

    _buehneUrl(farbe, breite) {
      const f = this.cfg.farben[farbe] || {};
      return f.buehne ? cdnBreite(f.buehne, breite) : null;
    }

    async _ladeBuehne(farbe, prio = 'auto') {
      const breite = Math.min(2000, Math.max(800, Math.ceil(((this.clientWidth || 800) * (window.devicePixelRatio || 1)) / 200) * 200));
      const url = this._buehneUrl(farbe, breite);
      if (!url || this._bilder['buehne:' + farbe]) return;
      this._bilder['buehne:' + farbe] = await ladeBild(url, prio).catch(() => null);
      // Exportbild fürs Warenkorbbild: feste 1600er-Fassung (Bytes unabhängig vom Gerät)
      const ex = this._buehneUrl(farbe, 1600);
      ladeBild(ex, 'low').then((i) => { this._bilder['export:' + farbe] = i; }, () => {});
    }

    async _ladeGrafiken() {
      const jobs = [];
      for (const [key, g] of Object.entries(this.cfg.grafiken || {})) {
        if (!g.url || this._bilder[key]) continue;
        jobs.push(ladeBild(cdnBreite(g.url, Math.min(1200, g.canvas ? Math.ceil(g.canvas[0] * 2) : 800))).then((i) => { this._bilder[key] = i; }, () => {}));
      }
      await Promise.all(jobs);
    }

    _getoent(key, hex) {
      const k = key + hex;
      this._getoentCache = this._getoentCache || {};
      if (this._getoentCache[k]) return this._getoentCache[k];
      const src = this._bilder[key];
      if (!src) return null;
      this._getoentCache[k] = toenen(src, hex);
      return this._getoentCache[k];
    }

    _svgBild(liste) {
      const svg = svgDokument(liste, this.F);
      if (this._svgCache.has(svg)) return this._svgCache.get(svg);
      const p = new Promise((res) => {
        const i = new Image();
        i.onload = () => res(i);
        i.onerror = () => res(null);
        i.src = svgDatenUrl(svg);
      });
      if (this._svgCache.size > 60) this._svgCache.clear();
      this._svgCache.set(svg, p);
      return p;
    }

    // Szene rechnen (synchron) — Grundlage für Vorschau, Vertrag und Eigenschaften
    _szene() {
      const sz = baueSzene(this.cfg, this.state, this.M, this.F);
      for (const e of sz.elemente) if (e.typ === 'text') e._lauf = textLauf(this.M, e.font, e.size, e.text, e.ls);
      return sz;
    }

    _zeichneAuf(ctx, sz, pxW, pxH, buehne) {
      return zeichneSzene(ctx, sz, pxW, pxH, {
        F: this.F, buehne, grundfarbe: (this.cfg.farben[this.state.farbe] || {}).druck, familie,
        bildFuer: (e) => (e.hex ? this._getoent(e.key, e.hex) : this._bilder[e.key]),
        svgFuer: (liste) => this._svgBild(liste),
      });
    }

    _planeZeichnen() {
      if (this._raf) return;
      this._raf = requestAnimationFrame(() => { this._raf = 0; this._zeichne(); });
    }

    async _zeichne() {
      const cv = this._canvas;
      if (!cv || !cv.parentElement) return;
      const cssW = cv.parentElement.clientWidth;
      if (!cssW) { if ((this._retry = (this._retry || 0) + 1) < 120) requestAnimationFrame(() => this._zeichne()); return; }
      const dpr = window.devicePixelRatio || 1;
      const w = Math.round(cssW * dpr), h = Math.round(w * BK.H / BK.W);
      const sz = this._letzteSzene || this._szene();
      const nr = (this._zeichenNr = (this._zeichenNr || 0) + 1);
      const off = document.createElement('canvas');
      off.width = w; off.height = h;
      await this._zeichneAuf(off.getContext('2d'), sz, w, h, this._bilder['buehne:' + this.state.farbe]);
      if (nr !== this._zeichenNr) return;                     // inzwischen neuer Stand
      if (cv.width !== w || cv.height !== h) { cv.width = w; cv.height = h; cv.style.height = (h / dpr) + 'px'; }
      const c2 = cv.getContext('2d');
      c2.drawImage(off, 0, 0);
      this._markiere(c2, sz, w, h);
      this._miniBlit();
      this._galVorschauBlit();
    }

    // fm8-shop1 (D): ausgewähltes Mitglied/Rotkehlchen auf der Bühne sichtbar markieren — nur Anzeige, nie Warenkorbbild/Vertrag.
    _markiere(ctx, sz, w, h) {
      const a = this._aktiv;
      if (!a || !sz.treffer) return;
      const t = sz.treffer.find((x) => x.art === a.art && x.i === a.i);
      if (!t) return;
      const k = w / BK.W, pad = 10;
      const x = (t.x - pad) * k, y = (t.y - pad) * k, bw = (t.w + 2 * pad) * k, bh = (t.h + 2 * pad) * k, r = 14 * k;
      const pfad = () => { ctx.beginPath(); if (ctx.roundRect) ctx.roundRect(x, y, bw, bh, r); else ctx.rect(x, y, bw, bh); };
      ctx.save();
      ctx.lineWidth = Math.max(3, 7 * k); ctx.strokeStyle = 'rgba(0,0,0,.45)'; pfad(); ctx.stroke();
      ctx.lineWidth = Math.max(2, 4 * k); ctx.strokeStyle = '#ffd34d'; ctx.setLineDash([12 * k + 4, 8 * k + 3]); pfad(); ctx.stroke();
      ctx.restore();
    }

    // Jede Änderung: Szene + Formular SOFORT, Zeichnen per rAF (Kaufweg hängt nie an Bildern).
    _aenderung() {
      this._fotoAus();
      this._letzteSzene = this._szene();
      this._syncFormular();
      this._planeZeichnen();
      this._zaehlerAktualisieren();
      this._symbolFelder();
    }

    // Symbolkacheln mit Farbvarianten: Feld in Mattenfarbe nachziehen (weiß auf weißer Kachel wäre unsichtbar).
    _symbolFelder() {
      const fa = (this.cfg.farben || {})[this.state.farbe];
      const hex = (fa && fa.druck) || '#161616';
      this.querySelectorAll('[data-bk-symfeld]').forEach((n) => n.setAttribute('fill', hex));
      this.querySelectorAll('[data-bk-spur]').forEach((n) => { n.style.background = (fa && fa.spuren) || '#8f8f8f'; });   // fm8-shop1 (F)
      this.querySelectorAll('[data-bk-matfeld]').forEach((n) => { n.style.background = hex; });                              // fm8-shop3 (B)
    }

    _syncFormular() {
      const form = this._form();
      if (!form) return;
      const v = findeVariante(this.product, this.state.groesse, this.state.farbe, BK_OPTION, this._achsen());
      if (v) {
        const id = form.querySelector('input[name="id"]');
        if (id) id.value = String(v.id);
        this._notiz(v.available ? '' : (this._s().unavailable || 'Diese Kombination ist gerade nicht verfügbar.'));
      }
      const vertrag = bauVertrag(this.cfg, this.state, this._letzteSzene);
      const props = bauEigenschaften(this.cfg, this.state, vertrag, this._eigOpt());
      form.querySelectorAll('input[data-bk-prop]').forEach((n) => n.remove());
      for (const [k, val] of Object.entries(props)) {
        if (val == null || val === '') continue;
        const i = document.createElement('input');
        i.type = 'hidden'; i.name = 'properties[' + k + ']'; i.value = val;
        i.setAttribute('data-bk-prop', '');
        form.appendChild(i);
      }
    }

    // Textmatten (T4): weitere Variantenachse aus dem Metafeld (z. B. { Schriftfarbe: 'grün' }), sonst null
    _achsen() {
      const t = this.cfg.textfarbe;
      return t && t.option ? { [t.option]: this.state.textfarbe } : null;
    }

    // Textmatten (T1/T4): Achsen ohne Shopify-Option gehen als sichtbare Eigenschaft in den Warenkorb
    _eigOpt() {
      const p = this.product;
      const t = this.cfg.textfarbe;
      return {
        farbeAlsEigenschaft: !!p && !hatOption(p, BK_OPTION.farbe) && Object.keys(this.cfg.farben || {}).length > 1,
        textfarbeAlsEigenschaft: !!t && !!p && !hatOption(p, t.option),
      };
    }

    // ---------- Kaufweg (Muster ttg-family.js) ----------
    // fm8-fix5 (1c): Desktop ≥ 750 px — Titel (einzige H1), Preis, Bewertung und Highlights des Theme-Kopfs in die rechte Spalte
    // über Größe/Farbe, damit die Bühne ohne Scrollen sichtbar ist. Formular und Variantenwahl bleiben, wo sie sind (Kaufweg
    // unverändert). Schmaler als 750 px: alles zurück an den alten Platz (Handy wie bisher).
    _kopfNachOben() {
      const inhalt = document.querySelector('.product-information__grid .product-details .group-block-content');
      const side = this.querySelector('.ttg-bk__side');
      if (!inhalt || !side || !window.matchMedia) return;
      const teile = [...inhalt.children].filter((c) => !c.matches('variant-picker, .buy-buttons-block, .view-product-title, script'));
      if (!teile.length) return;
      const kopf = document.createElement('div');
      kopf.className = 'ttg-bk__kopf';
      const marken = teile.map((c) => { const m = document.createComment('ttg-bk-kopf'); c.before(m); return m; });
      const mq = window.matchMedia('(min-width: 750px)');
      const setze = () => {
        if (mq.matches) { teile.forEach((c) => kopf.appendChild(c)); side.prepend(kopf); document.body.classList.add('ttg-bk-kopf-oben'); }
        else { teile.forEach((c, i) => marken[i].after(c)); kopf.remove(); document.body.classList.remove('ttg-bk-kopf-oben'); }
      };
      setze();
      if (mq.addEventListener) mq.addEventListener('change', setze);
    }

    _scharfSchalten() {
      const form = this._form();
      if (!form || form.__ttgBkArmed) return;
      form.__ttgBkArmed = true;
      const btn = this.querySelector('[data-bk-add]');
      const wrap = this.querySelector('[data-bk-cta]');
      if (btn && wrap) {
        let id = form.getAttribute('id');
        if (!id) { id = 'ttg-baukasten-native-form'; form.setAttribute('id', id); }
        btn.setAttribute('form', id);
        wrap.hidden = false;
      }
      document.addEventListener('click', (e) => {
        const b = e.target && e.target.closest ? e.target.closest('[ref="addToCartButton"], button[name="add"], button[type="submit"], input[type="submit"]') : null;
        if (!b || !(form.contains(b) || b.form === form)) return;
        if (!this._gate(true)) { e.preventDefault(); e.stopPropagation(); return; }
        if (this._beispielHinweis(b)) { e.preventDefault(); e.stopPropagation(); return; }
        this._vorbereiten(form);
        this._vorbereitet = true;
        setTimeout(() => { this._vorbereitet = false; }, 0);
      }, true);
      form.addEventListener('submit', (e) => {
        if (!this._gate(true)) { e.preventDefault(); e.stopImmediatePropagation(); return; }
        if (!this._vorbereitet && this._beispielHinweis(null)) { e.preventDefault(); e.stopImmediatePropagation(); return; }
        if (!this._vorbereitet) this._vorbereiten(form);
      }, true);
      document.addEventListener('cart:update', () => this._nachKauf());
      document.addEventListener('cart:error', () => { this._auftrag = null; });
    }

    // fm8-shop4 (C): noch unveränderte Beispielnamen → freundliche Rückfrage mit „Ändern“ / „So bestellen“ (kein Blockieren)
    _beispielHinweis(knopf) {
      if (this._bspOk) return false;
      const offen = this._offeneBeispiele();
      if (!offen.length) return false;
      let box = this.querySelector('[data-bk-bsp-hinweis]');
      if (!box) {
        box = el('div', 'ttg-bk__bsp-hinweis');
        box.setAttribute('data-bk-bsp-hinweis', ''); box.setAttribute('role', 'alertdialog');
        const cta = this.querySelector('[data-bk-cta]');
        const add = this.querySelector('[data-bk-add]');
        if (cta && add) cta.insertBefore(box, add); else (cta || this).appendChild(box);
      }
      box.textContent = '';
      const t = el('p', 'ttg-bk__bsp-text', 'Du hast noch Beispielnamen: ' + offen.join(', ') + '. Trotzdem so bestellen?');
      t.id = 'ttg-bk-bsp-text'; box.setAttribute('aria-labelledby', t.id);
      const reihe = el('div', 'ttg-bk__bsp-knoepfe');
      const aendern = el('button', 'ttg-bk__bsp-aendern', 'Ändern'); aendern.type = 'button';
      const so = el('button', 'ttg-bk__bsp-so', 'So bestellen'); so.type = 'button';
      aendern.addEventListener('click', () => {
        box.hidden = true;
        const f = [...this.querySelectorAll('input.ist-beispiel')][0];
        if (f) { try { f.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) { f.scrollIntoView(); } f.focus({ preventScroll: true }); try { f.select(); } catch (e) { /* egal */ } }
      });
      so.addEventListener('click', () => {
        this._bspOk = true; box.hidden = true;
        const k = knopf || this.querySelector('[data-bk-add]');
        if (k) k.click();
      });
      reihe.append(aendern, so);
      box.append(t, reihe);
      box.hidden = false;
      try { box.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) { /* alt */ }
      so.focus({ preventScroll: true });
      return true;
    }

    _gate(zeigen) {
      const g = pruefeZustand(this.cfg, this.state);
      const box = this.querySelector('[data-bk-error]');
      if (g.ok) { if (box) { box.hidden = true; box.textContent = ''; } return true; }
      if (zeigen && box) {
        box.textContent = g.fehler.map((f) => f.text).join(' ');
        box.hidden = false;
        try { box.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) { /* alt */ }
      }
      return false;
    }

    _vorbereiten(form) {
      this._letzteSzene = this._szene();
      this._syncFormular();
      const g = neueGruppe();
      form.querySelectorAll('input[data-bk-group]').forEach((n) => n.remove());
      const i = document.createElement('input');
      i.type = 'hidden'; i.name = 'properties[_ttg_group]'; i.value = g; i.setAttribute('data-bk-group', '');
      form.appendChild(i);
      this._auftrag = null;
      this._snapLaeuft = false;
      if (this.previewUpload && this.previewUpload.enabled) {
        // Snapshot asynchron (SVG-Bilder sind aus der Bühne im Cache, dauert Millisekunden).
        this._snapLaeuft = true;
        this._snapshot().then((url) => { this._auftrag = vorschauAuftrag(g, url, this.previewUpload); },
          (e) => console.warn('[ttg-baukasten] Vorschaubild fehlgeschlagen — Kauf läuft ohne:', e))
          .then(() => { this._snapLaeuft = false; if (this._kaufFertig) this._nachKauf(); });
      }
    }

    // Warenkorbbild: quadratisch 1000 px, Bühne (mit Gummirand) mittig auf Weiß — wie ttg-family.js
    async _snapshot() {
      const pu = this.previewUpload || {};
      const w = pu.maxWidth || 1000;
      const c = document.createElement('canvas');
      c.width = w; c.height = w;
      const ctx = c.getContext('2d');
      ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, w, w);
      const h = Math.round(w * BK.H / BK.W);
      ctx.save(); ctx.translate(0, Math.round((w - h) / 2));
      await this._zeichneAuf(ctx, this._letzteSzene, w, h, this._bilder['export:' + this.state.farbe] || this._bilder['buehne:' + this.state.farbe]);
      ctx.restore();
      return c.toDataURL(pu.format || 'image/jpeg', pu.quality != null ? pu.quality : 0.85);
    }

    _nachKauf() {
      this._kaufFertig = true;
      const job = this._auftrag;
      if (!job && this._snapLaeuft) {
        // Snapshot noch nicht fertig: er ruft _nachKauf selbst wieder auf; Sicherung nach 3 s ohne Bild.
        if (!this._frist) this._frist = setTimeout(() => { if (this._snapLaeuft) this._zumWarenkorb(); }, 3000);
        return;
      }
      if (!job) { this._zumWarenkorb(); return; }
      this._auftrag = null;
      let ok = false;
      try {
        const up = window.__ttgPreviewUploader;
        if (up && typeof up.stash === 'function') ok = up.stash(job) === true;
        else if (!sessionStorage.getItem(BK_STASH)) { sessionStorage.setItem(BK_STASH, JSON.stringify(job)); ok = sessionStorage.getItem(BK_STASH) !== null; }
      } catch (e) { ok = false; }
      if (ok) { this._zumWarenkorb(); return; }
      const up = window.__ttgPreviewUploader;
      if (!up || typeof up.nachtragen !== 'function') { this._zumWarenkorb(); return; }
      let fertig = false;
      const lauf = Promise.resolve(up.nachtragen(job)).then(() => { fertig = true; }).catch((e) => console.warn('[ttg-baukasten] Nachtrag fehlgeschlagen:', e));
      Promise.race([lauf, new Promise((r) => setTimeout(r, BK_PREVIEW_TIMEOUT_MS))]).then(() => {
        if (!fertig) console.warn('[ttg-baukasten] Vorschaubild-Nachtrag nicht rechtzeitig fertig.');
        this._zumWarenkorb();
      });
    }

    _zumWarenkorb() {
      if (this._weiter) return;
      this._weiter = true;
      const ziel = (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || '/cart';
      window.location.replace(ziel);
    }

    // ---------- Bedienung ----------
    _baueUI() {
      const host = this.querySelector('[data-bk-ui]');
      host.textContent = '';
      // fm8-shop3 (G): Größe → Mattenfarbe → Texte → Wie viele seid ihr? → Mitglieder → Symbol/Extras → Warenkorb
      host.append(this._groesseFarbe(), this._felder());
      if (this.cfg.textfarbe) host.appendChild(this._textfarbeWahl());   // Textmatten (T4)
      if (this.cfg.mitglieder) host.appendChild(this._mitgliederFeld());
      if (this.cfg.menschen) host.appendChild(this._namenBloecke({ schluessel: 'menschen', label: this.cfg.menschen.label || 'Namen', frage: 'Wie viele seid ihr?', wort: 'Name', min: 1, max: this.cfg.menschen.max, art: 'person' }));
      if (this.cfg.hunde) host.appendChild(this._namenBloecke({ schluessel: 'hunde', label: this.cfg.hunde.label || 'Hundenamen', frage: 'Wie viele Hunde?', wort: 'Hund', min: this.cfg.hunde.min || 1, max: this.cfg.hunde.max, art: 'tier' }));
      if (this.cfg.rollen) host.appendChild(this._rollenFeld());
      if (this.cfg.symbol) host.appendChild(this._symbolWahl());
      if (this.cfg.rotkehlchen) host.appendChild(this._rotkehlchenFeld());
    }

    // fm8-shop4 (A/C): Beispielnamen — vorausgefüllte Werte (Startbeispiel der Matte + beim Erhöhen ergänzte) tragen das Etikett
    // „Beispiel“, solange sie unverändert sind; am Warenkorb gibt es dann einen freundlichen Hinweis (blockiert nie).
    _bspMenge() {
      if (!this._bsp) {
        const b = this.cfg.beispiel || {};
        this._bsp = new Set([...(b.mitglieder || []).map((m) => m.name), ...(b.menschen || []), ...(b.hunde || []),
          ...(b.rollen || []).flatMap((r) => [r.name, r.rolle])].map((x) => String(x || '').trim()).filter(Boolean));
      }
      return this._bsp;
    }
    _vergebeneNamen() {
      const st = this.state;
      return [...(st.mitglieder || []).map((m) => m.name), ...(st.menschen || []), ...(st.hunde || []), ...(st.rollen || []).flatMap((r) => [r.name, r.rolle])];
    }
    _neuerBeispielname(art) {
      const n = beispielName(BK_BEISPIEL[art], this._vergebeneNamen());
      if (n) this._bspMenge().add(n);
      return n;
    }
    _offeneBeispiele() {
      const bsp = this._bspMenge();
      const st = this.state;
      const namen = [...(st.mitglieder || []).map((m) => m.name), ...(st.menschen || []), ...(st.hunde || []), ...(st.rollen || []).map((r) => r.name)];
      const offen = namen.map((x) => String(x || '').trim()).filter((x) => x && bsp.has(x));
      // Textmatten Pilot 2: Felder mit "beispiel": true zählen mit, solange ihr Wert unverändert der Vorgabe entspricht (nie feste Felder)
      for (const f of this.cfg.felder || []) {
        if (f.beispiel !== true || f.fest) continue;
        if (istOhneText(st, f)) continue;                     // Opt-out „Kein Text“: kein Beispielname offen
        const v = String((st.felder || {})[f.id] ?? '').trim();
        if (v && v === String(f.vorgabe || '').trim()) offen.push(v);
      }
      return [...new Set(offen)];
    }

    _feldRahmen(label) {
      const f = el('div', 'ttg-bk__field');
      f.appendChild(el('span', 'ttg-bk__label', label));
      return f;
    }

    _groesseFarbe() {
      const wrap = el('div', 'ttg-bk__top');
      const g = this._feldRahmen('Größe');
      g.classList.add('ttg-bk__field--groesse');
      const kacheln = el('div', 'ttg-bk__tiles ttg-bk__tiles--groesse');   // fm8-shop1 (B): Desktop untereinander, Handy nebeneinander
      for (const key of ['40x60', '50x75']) {
        const v = findeVariante(this.product, key, this.state.farbe, BK_OPTION, this._achsen());
        if (this.product && !v) continue;
        const b = el('button', 'ttg-bk__tile');
        b.type = 'button';
        b.setAttribute('aria-pressed', String(key === this.state.groesse));
        b.appendChild(el('span', 'ttg-bk__tile-v', key.replace('x', ' × ') + ' cm'));
        if (v && v.price != null) b.appendChild(el('span', 'ttg-bk__tile-p', formatEUR(v.price)));
        b.addEventListener('click', () => { this.state.groesse = key; this._oben(); this._aenderung(); });
        kacheln.appendChild(b);
      }
      g.appendChild(kacheln);
      // Textmatten (T1): ohne Option „Farbe“ wählt der Baukasten die Mattenfarbe selbst — bei nur einer Farbe entfällt die Wahl
      const farbeOpt = !this.product || hatOption(this.product, BK_OPTION.farbe);
      if (!farbeOpt && Object.keys(this.cfg.farben).length < 2) {
        wrap.append(g);
        this._obenEl = wrap;
        return wrap;
      }
      const f = this._feldRahmen(farbeOpt ? 'Mattenfarbe' : (this.cfg.farbe_label || 'Mattenfarbe'));
      const sw = el('div', 'ttg-bk__tiles');
      for (const [farbe, d] of Object.entries(this.cfg.farben)) {
        if (this.product && farbeOpt && !findeVariante(this.product, this.state.groesse, farbe)) continue;
        const b = el('button', 'ttg-bk__tile ttg-bk__tile--farbe');
        b.type = 'button';
        b.setAttribute('aria-pressed', String(farbe === this.state.farbe));
        const pkt = el('span', 'ttg-bk__swatch'); pkt.style.background = d.druck;
        b.append(pkt, el('span', 'ttg-bk__tile-v', farbe));
        b.addEventListener('click', async () => {
          this.state.farbe = farbe; this._oben(); this._aenderung();
          await this._ladeBuehne(farbe); this._planeZeichnen();
        });
        sw.appendChild(b);
      }
      f.appendChild(sw);
      wrap.append(g, f);
      this._obenEl = wrap;
      return wrap;
    }

    _oben() {
      const neu = this._groesseFarbe();
      this._obenEl.replaceWith(neu);
      this._obenEl = neu;
    }

    _eingabe({ wert, max, zeilen = 1, placeholder, label, onInput, weiter, beispiel, bspWert, font }) {
      const fehlen = fehlendeZeichen(this.cfg, font);        // Textmatten Teil 2: Zeichensperre je Schrift
      const box = el('div', 'ttg-bk__inbox');
      const inp = zeilen > 1 ? document.createElement('textarea') : document.createElement('input');
      inp.className = 'ttg-bk__input';
      if (zeilen > 1) { inp.rows = 2; } else inp.type = 'text';
      inp.value = wert || '';
      if (placeholder) inp.placeholder = placeholder;
      if (label) inp.setAttribute('aria-label', label);
      inp.setAttribute('autocomplete', 'off');
      inp.maxLength = max * 2 + 2;              // Zeichen zählt pruefeEingabe (Emoji = 1), das hier ist nur die Notbremse
      const zahl = el('span', 'ttg-bk__count');
      const fehler = el('p', 'ttg-bk__err'); fehler.hidden = true;
      const upd = () => {
        const p = pruefeEingabe(inp.value, { max, zeilen, fehlen });
        zahl.textContent = p.laenge + '/' + max;
        zahl.classList.toggle('is-voll', p.laenge > max);
        fehler.textContent = p.fehler; fehler.hidden = !p.fehler;
        inp.classList.toggle('is-fehler', !!p.fehler);
        return p;
      };
      let etikett = null;
      if (beispiel) {
        etikett = el('span', 'ttg-bk__bsp', 'Beispiel');
        etikett.setAttribute('aria-hidden', 'true');
        // bspWert (Textmatten Pilot 2): Feld mit "beispiel": true — Beispiel ist genau seine Vorgabe, nicht die Namensmenge
        const pruef = () => { const an = bspWert != null ? inp.value.trim() === bspWert : this._bspMenge().has(inp.value.trim()); etikett.hidden = !an; inp.classList.toggle('ist-beispiel', an); };
        inp.addEventListener('input', pruef);
        inp._bkBspPruef = pruef;
      }
      inp.addEventListener('input', () => { const p = upd(); onInput(p.wert); });
      if (weiter && zeilen === 1) {
        // fm8-shop3 (F): Tastatur-Knopf „Weiter“ springt ins nächste Namensfeld, im letzten „Fertig“
        inp.setAttribute('data-bk-weiter', '');
        inp.enterKeyHint = 'next';
        inp.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter' || e.isComposing) return;
          e.preventDefault();
          const alle = [...this.querySelectorAll('input[data-bk-weiter]')];
          const n = alle[alle.indexOf(inp) + 1];
          if (n) { n.focus(); try { n.select(); } catch (err) { /* egal */ } } else inp.blur();
        });
        inp.addEventListener('focus', () => { const alle = [...this.querySelectorAll('input[data-bk-weiter]')]; inp.enterKeyHint = alle[alle.length - 1] === inp ? 'done' : 'next'; });
      }
      upd();
      box.append(inp, zahl);
      if (etikett) { box.appendChild(etikett); inp._bkBspPruef(); }
      const w = el('div', 'ttg-bk__inwrap');
      w.append(box, fehler);
      w._inp = inp; w._upd = upd;
      return w;
    }

    _felder() {
      const wrap = el('div', 'ttg-bk__group');
      for (const f of this.cfg.felder || []) {
        if (f.fest) continue;                                 // Textmatten (T3): feste Zeile hat kein Eingabefeld
        const r = this._feldRahmen(f.label + (f.pflicht || hatOptOut(f) ? '' : ' (optional)'));   // Opt-out: Label wie die Engine, ohne „(optional)“
        const bsp = f.beispiel === true && f.vorgabe ? { beispiel: true, bspWert: String(f.vorgabe).trim() } : {};   // Textmatten Pilot 2, nur mit Schalter
        const e = this._eingabe({ wert: this.state.felder[f.id], max: f.max, zeilen: f.zeilen || 1, placeholder: f.platzhalter, label: f.label, font: f.font, ...bsp,
          onInput: (v) => { this.state.felder[f.id] = v; this._aenderung(); } });
        r.appendChild(e);
        if ((f.zeilen || 1) > 1) r.appendChild(el('p', 'ttg-bk__hint', 'Mit der Eingabetaste kannst du auf zwei Zeilen umbrechen.'));
        if (hatOptOut(f)) r.appendChild(this._keinTextKaestchen(f, e));
        wrap.appendChild(r);
      }
      return wrap;
    }

    // Opt-out „Kein Text gewünscht“ (wie ttg-engine.js _bodyText/no-text): Kästchen unter dem Feld, Optik wie .ttg-check der Engine
    // (inline, damit keine CSS-Datei mitwandern muss). Angehakt: Feld gedimmt wie .ttg-field--locked, aber NICHT disabled — Fokus ins
    // Feld hebt den Haken auf (Engine focusin). Abhaken: Feld fokussiert, Caret ans Ende, vorheriger Wert bleibt.
    _keinTextKaestchen(f, wrap) {
      const inp = wrap._inp;
      const lab = el('label', 'ttg-bk__keintext');
      lab.style.cssText = 'display:flex;align-items:center;gap:8px;font-size:12.5px;color:var(--ttg-muted);font-weight:600;margin-top:10px;cursor:pointer';
      const cb = document.createElement('input');
      cb.type = 'checkbox'; cb.setAttribute('data-bk-keintext', f.id);
      cb.style.cssText = 'width:16px;height:16px;margin:0;accent-color:var(--ttg-accent)';
      lab.append(cb, document.createTextNode(' ' + BK_KEIN_TEXT_CTA));
      const etikett = wrap.querySelector('.ttg-bk__bsp');
      const zeige = () => {
        const aus = istOhneText(this.state, f);
        cb.checked = aus;
        inp.classList.toggle('ist-ohne-text', aus);
        inp.style.background = aus ? 'var(--ttg-soft)' : '';
        inp.style.color = aus ? '#aaa' : '';
        if (etikett) { if (aus) etikett.hidden = true; else if (inp._bkBspPruef) inp._bkBspPruef(); }
      };
      cb.addEventListener('change', () => {
        this.state.ohneText[f.id] = cb.checked;
        zeige();
        if (!cb.checked) { inp.focus(); const n = inp.value.length; try { inp.setSelectionRange(n, n); } catch (err) { /* egal */ } }
        this._aenderung();
      });
      inp.addEventListener('focus', () => {
        if (!istOhneText(this.state, f)) return;
        this.state.ohneText[f.id] = false;
        zeige();
        const n = inp.value.length; try { inp.setSelectionRange(n, n); } catch (err) { /* egal */ }
        this._aenderung();
      });
      zeige();
      return lab;
    }

    // Textmatten (T4): Textfarbe als Farbkacheln; führt das Produkt die Option, nur Werte mit Variante zur gewählten Größe
    _textfarbeWahl() {
      const t = this.cfg.textfarbe;
      const r = this._feldRahmen(t.label || t.option || 'Schriftfarbe');
      const kacheln = el('div', 'ttg-bk__tiles');
      const optionDa = !!this.product && hatOption(this.product, t.option);
      for (const [wert, hex] of Object.entries(t.werte || {})) {
        if (optionDa && !findeVariante(this.product, this.state.groesse, this.state.farbe, BK_OPTION, { [t.option]: wert })) continue;
        const b = el('button', 'ttg-bk__tile ttg-bk__tile--farbe');
        b.type = 'button';
        b.setAttribute('aria-pressed', String(wert === this.state.textfarbe));
        const pkt = el('span', 'ttg-bk__swatch'); pkt.style.background = hex;
        b.append(pkt, el('span', 'ttg-bk__tile-v', (t.namen || {})[wert] || wert));
        b.addEventListener('click', () => {
          this.state.textfarbe = wert;
          kacheln.querySelectorAll('button').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
          this._oben(); this._aenderung();
        });
        kacheln.appendChild(b);
      }
      r.appendChild(kacheln);
      return r;
    }

    _symbolWahl() {
      const r = this._feldRahmen('Symbol');
      const t = el('div', 'ttg-bk__tiles');
      const sym = this.cfg.symbol;
      const varianten = sym.werte.some((w) => String(w).includes('-'));
      const fa = (this.cfg.farben || {})[this.state.farbe];
      const matte = (fa && fa.druck) || '#161616';
      for (const k of sym.werte) {
        const b = el('button', 'ttg-bk__tile ttg-bk__tile--bild');
        b.type = 'button';
        b.setAttribute('aria-pressed', String(k === this.state.symbol));
        const text = symbolText(sym, k);
        b.setAttribute('aria-label', text);
        const herz = symbolForm(k) === 'herz';
        let inhalt;
        if (varianten) {                                              // M1: Symbol in Druckfarbe auf Feld in Mattenfarbe
          const hex = (typeof sym.farbe === 'string' ? sym.farbe : (sym.farbe || {})[k]) || BK.TEXTFARBE;
          inhalt = '<rect data-bk-symfeld x="-58" y="-58" width="116" height="116" rx="16" fill="' + matte + '"/>'
            + (k === 'keins' ? '<path d="M-28 28L28 -28" stroke="#8a8a8a" stroke-width="7" stroke-linecap="round"/>' : herz ? this.F.heart(0, 0, 76, hex) : this.F.paw(0, 0, 70, hex));
        } else {
          inhalt = herz ? this.F.heart(0, 0, 100, '#2b2b2b') : this.F.paw(0, 0, 92, '#2b2b2b');
        }
        const svg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="-60 -60 120 120" width="44" height="44">' + inhalt + '</svg>';
        const span = el('span', 'ttg-bk__tile-img'); span.innerHTML = svg;     // eigener Generator, keine Nutzerdaten
        b.append(span, el('span', 'ttg-bk__tile-v', text));
        b.addEventListener('click', () => { this.state.symbol = k; t.querySelectorAll('button').forEach((x) => x.setAttribute('aria-pressed', String(x === b))); this._aenderung(); });
        t.appendChild(b);
      }
      r.appendChild(t);
      return r;
    }

    _typBild(typ, m) {
      const mg = this.cfg.mitglieder;
      const t = mg.typen[typ];
      const span = el('span', 'ttg-bk__tile-img');
      if (this.cfg.anordnung === 'reihe-figur') {
        const [svg] = t.tier ? this.F.dogBack(t.fell || 'golden', t.ohren || 'schlapp', t.groesse || 'gross', null, !!t.katze)
          : this.F.personBack(t.kind, t.haar || 'kurz', t.haarfarbe || 'braun', t.oberteil || '#c2453f', t.hose || '#4a6a9a', t.haut || 'hell', t.hoehe);
        span.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="-260 -470 520 500" width="52" height="50"><defs>' + this.F.SHADE + '</defs>' + svg + '</svg>';
        return span;
      }
      if (this.cfg.anordnung === 'stiefel') {                    // M9: Kachel = Paar in Vorgabe- bzw. gewählter Farbe (m)
        const farbe = (m && m.farbe) || t.farbe, muster = (m && m.muster) || t.muster;
        const id = '-k' + typ + (m ? 'm' + this.state.mitglieder.indexOf(m) : '');
        let svg = t.art === 'stiefel' ? this.F.S.render(t.modell, { pattern: muster, colors: mg.stiefel[farbe], id })
          : t.art === 'napf' ? this.F.S.render('napf', { colors: mg.napf[farbe] }) : this.F.S.render('pfoten', { colors: { main: mg.pfoten[farbe] } });
        // Napf und Pfotenspur füllen ihre 400er-Fläche nur unten bzw. in der Mitte: für die Kachel zuschneiden
        if (t.art === 'napf') svg = svg.replace('viewBox="0 0 200 400"', 'viewBox="0 290 200 110"');
        if (t.art === 'pfotenspur') svg = svg.replace('viewBox="0 0 160 400"', 'viewBox="10 160 140 160"');
        span.innerHTML = svg.replace('<svg ', '<svg style="height:48px;width:auto" ');   // eigener Generator, keine Nutzerdaten
        return span;
      }
      const key = t.vorschau_grafik || t.grafik;
      const g = (this.cfg.grafiken || {})[key];
      if (g && g.url) { const i = document.createElement('img'); i.src = cdnBreite(g.url, 160); i.alt = ''; i.loading = 'lazy'; span.appendChild(i); }
      return span;
    }

    // fm8-shop3 (A): „Wie viele …?“ als Zahlenkacheln. Erhöhen hängt Mitglieder mit Vorgabe-Typ an (Name leer, Beispiel als
    // Platzhalter), Verringern nimmt die letzten weg. Ersetzt „Mitglied hinzufügen“ und die ✕ je Karte.
    _anzahl(label, min, max, wert, setze) {
      const w = el('div', 'ttg-bk__anzahl');
      const id = 'ttg-bk-anz-' + Math.random().toString(36).slice(2, 8);
      const lab = el('span', 'ttg-bk__label', label); lab.id = id;
      const reihe = el('div', 'ttg-bk__zahlen');
      reihe.setAttribute('role', 'group'); reihe.setAttribute('aria-labelledby', id);
      for (let n = Math.max(1, min); n <= max; n++) {
        const b = el('button', 'ttg-bk__zahl', String(n)); b.type = 'button';
        b.setAttribute('aria-pressed', String(n === wert));
        b.addEventListener('click', () => {
          reihe.querySelectorAll('button').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
          setze(n);
        });
        reihe.appendChild(b);
      }
      w.append(lab, reihe);
      return w;
    }

    // fm8-shop3 (Jens 17.10.): wischbare Reihe zeigt links/rechts einen Ausblendrand, solange es dort weitergeht
    _randBeobachten(r) {
      if (!r || r._bkRand) return;
      r._bkRand = true;
      const setze = () => {
        const mehr = r.scrollWidth - r.clientWidth > 2;
        r.classList.toggle('kann-links', mehr && r.scrollLeft > 2);
        r.classList.toggle('kann-rechts', mehr && r.scrollLeft + r.clientWidth < r.scrollWidth - 2);
      };
      r.addEventListener('scroll', setze, { passive: true });
      if (window.ResizeObserver) new ResizeObserver(setze).observe(r);
      requestAnimationFrame(setze);
      r._bkRandSetze = setze;
    }

    // fm8-shop3 (D): kleines Menü „⋯“ je Block mit nach links / nach rechts / entfernen
    _menue(titel, i, n, { links, rechts, weg, wegAus }) {
      const d = el('details', 'ttg-bk__menue');
      const s = el('summary', null, '⋯');
      s.setAttribute('aria-label', 'Aktionen für ' + titel);
      d.appendChild(s);
      const liste = el('div', 'ttg-bk__menue-liste');
      const knopf = (text, fn, aus) => { const b = el('button', 'ttg-bk__menue-punkt', text); b.type = 'button'; b.disabled = !!aus; b.addEventListener('click', () => { d.open = false; fn(); }); liste.appendChild(b); };
      knopf('◀ nach links', links, i === 0);
      knopf('nach rechts ▶', rechts, i === n - 1);
      knopf('✕ entfernen', weg, !!wegAus);
      d.appendChild(liste);
      d.addEventListener('toggle', () => { if (d.open) this.querySelectorAll('.ttg-bk__menue[open]').forEach((x) => { if (x !== d) x.open = false; }); });
      return d;
    }

    _mitgliederFeld() {
      const mg = this.cfg.mitglieder;
      const r = this._feldRahmen(mg.label || 'Familienmitglieder');
      r.classList.add('ttg-bk__field--mitglieder');
      const tierMatte = this.cfg.anordnung === 'herzen' ? false : Object.keys(mg.typen).every((k) => istHaustier(this.cfg, k));
      this._mAnzahlBox = el('div');
      const anzahl = () => this._anzahl(tierMatte ? 'Wie viele Tiere?' : 'Wie viele seid ihr?', 1, mg.max, this.state.mitglieder.length, (n) => {
        const ms = this.state.mitglieder;
        while (ms.length < n) { const typ = vorgabeTyp(this.cfg, ms.length); ms.push({ typ, name: this._neuerBeispielname(istHaustier(this.cfg, typ) ? 'tier' : 'person') }); }
        while (ms.length > n) ms.pop();
        this._aktiv = null;
        this._mitgliederZeilen();
        this._aenderung();
      });
      this._mAnzahlNeu = () => { this._mAnzahlBox.textContent = ''; this._mAnzahlBox.appendChild(anzahl()); };
      this._mAnzahlNeu();
      r.appendChild(this._mAnzahlBox);
      this._mListe = el('div', 'ttg-bk__members ttg-bk__blocks');
      this._mHinweis = el('p', 'ttg-bk__hint');
      r.append(this._mListe, this._mHinweis);
      this._mitgliederZeilen();
      return r;
    }

    // fm8-shop3 (B/C): je Mitglied ein nummerierter Block (Nummer = Platz von links): Typ-Kacheln, Name, Optionen als Kacheln
    _mitgliederZeilen() {
      const mg = this.cfg.mitglieder;
      const ms = this.state.mitglieder;
      const BEISPIEL = { person: ['Markus', 'Anja', 'Lina', 'Ben', 'Mia', 'Paul', 'Emma', 'Tom'], tier: ['Bello', 'Mimi', 'Luna', 'Rocky', 'Kira'] };
      this._mListe.textContent = '';
      let nPerson = 0, nTier = 0;
      ms.forEach((m, i) => {
        const tier = istHaustier(this.cfg, m.typ);
        const titel = (i + 1) + '. ' + (tier ? 'Haustier' : 'Person');
        const bsp = tier ? BEISPIEL.tier[nTier++ % BEISPIEL.tier.length] : BEISPIEL.person[nPerson++ % BEISPIEL.person.length];
        const row = el('div', 'ttg-bk__member ttg-bk__block');
        row.dataset.bkMitglied = String(i);
        if (this._aktiv && this._aktiv.art === 'mitglied' && this._aktiv.i === i) row.classList.add('is-aktiv');
        row.addEventListener('focusin', () => this._waehle({ art: 'mitglied', i }, false));
        const kopf = el('div', 'ttg-bk__block-kopf');
        kopf.append(el('span', 'ttg-bk__block-nr', titel), this._menue(titel, i, ms.length, {
          links: () => { [ms[i - 1], ms[i]] = [ms[i], ms[i - 1]]; this._aktiv = null; this._mitgliederZeilen(); this._aenderung(); },
          rechts: () => { [ms[i + 1], ms[i]] = [ms[i], ms[i + 1]]; this._aktiv = null; this._mitgliederZeilen(); this._aenderung(); },
          weg: () => { ms.splice(i, 1); this._aktiv = null; this._mAnzahlNeu(); this._mitgliederZeilen(); this._aenderung(); },
          wegAus: ms.length <= 1,
        }));
        row.append(kopf, this._typWahl(m, i, titel));
        const nf = el('div', 'ttg-bk__namefeld');
        nf.appendChild(el('span', 'ttg-bk__sublabel', 'Name'));
        nf.appendChild(this._eingabe({ wert: m.name, max: this.cfg.regeln.namen_max, placeholder: 'z. B. ' + bsp, label: 'Name, ' + titel, weiter: true, beispiel: true, font: mg.name && mg.name.font,
          onInput: (v) => { m.name = v; this._aenderung(); if (this._vogelChips) this._vogelZeichnen(); } }));
        row.appendChild(nf);
        const opts = this._optionenSofort(m, i, row);
        if (opts) row.appendChild(opts);
        this._mListe.appendChild(row);
      });
      if (this._vogelChips) this._vogelZeichnen();
      // gewählte Kachel in jeder Reihe sichtbar (Handy: Reihe ist waagerecht wischbar, gewählter Typ kann rechts außerhalb liegen)
      requestAnimationFrame(() => this._mListe.querySelectorAll('.ttg-bk__typwahl').forEach((r) => {
        const k = r.querySelector('.ttg-bk__typ[aria-pressed="true"]');
        if (k && (k.offsetLeft + k.offsetWidth > r.scrollLeft + r.clientWidth || k.offsetLeft < r.scrollLeft)) r.scrollLeft = Math.max(0, k.offsetLeft - (r.clientWidth - k.offsetWidth) / 2);
        this._randBeobachten(r); r._bkRandSetze();
      }));
      this._mHinweis.textContent = 'Reihenfolge links → rechts wie auf der Matte · max. ' + this.cfg.regeln.namen_max + ' Zeichen je Name.';
      this._symbolFelder();
    }

    // fm8-shop3 (B): Typ-Auswahl direkt sichtbar als Bildkacheln (Handy: waagerecht wischbar), gewählte mit Häkchen,
    // Figur auf Feld in Mattenfarbe. M4: Mensch (Herz) / Hund (Pfote) / Katze (Pfote) — das ist zugleich die Wahl Herz/Pfote.
    _typWahl(m, i, titel) {
      const mg = this.cfg.mitglieder;
      const reihe = el('div', 'ttg-bk__typwahl');
      reihe.setAttribute('role', 'group');
      reihe.setAttribute('aria-label', 'Figur für ' + titel);
      for (const [typ, t] of Object.entries(mg.typen)) {
        const an = typ === m.typ;
        const b = el('button', 'ttg-bk__typ');
        b.type = 'button';
        b.dataset.bkTyp = typ;
        b.setAttribute('aria-pressed', String(an));
        const bild = this._typBild(typ, an ? m : null);
        bild.setAttribute('data-bk-matfeld', '');
        b.append(bild, el('span', 'ttg-bk__typ-v', t.label));
        b.addEventListener('click', () => {
          if (typ === m.typ) return;
          this.state.mitglieder[i] = typWechsel(this.cfg, m, typ);
          this._aktiv = { art: 'mitglied', i };
          this._mitgliederZeilen();
          this._aenderung();
          const neu = this._mListe.querySelector('[data-bk-mitglied="' + i + '"] .ttg-bk__typ[aria-pressed="true"]');
          if (neu) neu.focus({ preventScroll: true });
        });
        reihe.appendChild(b);
      }
      return reihe;
    }

    // fm8-shop3 (C): typabhängige Optionen direkt unter dem Namen als Kacheln (Farbe/Oberteil als Farbpunkte, sonst Chips).
    // M3: Paarfarben; M4: Farbe (Vorgabe automatisch je Platz).
    _optionenSofort(m, i, row) {
      const mg = this.cfg.mitglieder;
      const t = mg.typen[m.typ];
      const box = el('div', 'ttg-bk__opts ttg-bk__opts--sofort');
      const gruppe = (name, inhalt) => { const g = el('div', 'ttg-bk__opt ttg-bk__opt--inline'); g.append(el('span', 'ttg-bk__sublabel', name), inhalt); box.appendChild(g); };
      const bildNeu = () => {
        const k = row.querySelector('.ttg-bk__typ[aria-pressed="true"] .ttg-bk__tile-img');
        if (k) { const n = this._typBild(m.typ, m); n.setAttribute('data-bk-matfeld', ''); k.replaceWith(n); this._symbolFelder(); }
      };
      const farbpunkte = (werte, aktuell, hexVon, nameVon, setze) => {
        const r = el('span', 'ttg-bk__farben'); r.setAttribute('role', 'group');
        for (const wv of werte) {
          const b = el('button', 'ttg-bk__farbe'); b.type = 'button';
          b.style.background = hexVon(wv); b.title = nameVon(wv); b.setAttribute('aria-label', b.title);
          if (hexVon(wv) === null) b.setAttribute('data-bk-spur', '');
          b.setAttribute('aria-pressed', String(wv === aktuell));
          b.addEventListener('click', () => { setze(wv); r.querySelectorAll('button').forEach((x) => x.setAttribute('aria-pressed', String(x === b))); bildNeu(); this._aenderung(); });
          r.appendChild(b);
        }
        return r;
      };
      if (mg.paarfarben) {                                           // M3 (fm8-shop1 F)
        const spur = (this.cfg.farben[this.state.farbe] || {}).spuren || '#8f8f8f';
        const pf = mg.paarfarben;
        const aktuell = m.farbe && pf[m.farbe] ? m.farbe : Object.keys(pf).find((k) => !pf[k]);
        const r = farbpunkte(Object.keys(pf), aktuell, (k) => pf[k] || null, (k) => (mg.paarfarbnamen || {})[k] || k, (k) => { if (pf[k]) m.farbe = k; else delete m.farbe; });
        r.querySelectorAll('[data-bk-spur]').forEach((b) => { b.style.background = spur; });
        gruppe(mg.paarfarben_label || 'Farbe', r);
      }
      const o = t.optionen || {};
      for (const [feld, werte] of Object.entries(o)) {
        const name = (mg.optionsnamen || {})[feld] || (feld === 'farbe' ? 'Farbe' : feld);
        if (feld === 'farbe' || feld === 'oberteil') {
          const auto = this.cfg.anordnung === 'herzen' ? mg.palette[i % mg.palette.length] : '';
          const aktuell = m[feld] || t[feld] || auto || (feld === 'farbe' ? '' : werte[0]);
          const hex = (wv) => (feld === 'farbe' ? (mg.palette_hex || {})[wv] || (mg.stiefel && mg.stiefel[wv] && mg.stiefel[wv].main) || (mg.napf && mg.napf[wv] && mg.napf[wv].main) || (mg.pfoten && mg.pfoten[wv]) || wv : wv);
          gruppe(this.cfg.anordnung === 'herzen' ? 'Farbe' : name, farbpunkte(werte, aktuell, hex, (wv) => (mg.farbnamen || {})[wv] || wv, (wv) => { m[feld] = wv; }));
        } else {
          const r = el('span', 'ttg-bk__chips'); r.setAttribute('role', 'group'); r.setAttribute('aria-label', name);
          const aktuell = m[feld] || t[feld] || werte[0];
          for (const wv of werte) {
            const b = el('button', 'ttg-bk__chip ttg-bk__chip--klein', (mg.wertnamen || {})[wv] || wv); b.type = 'button';
            b.setAttribute('aria-pressed', String(wv === aktuell));
            b.addEventListener('click', () => { m[feld] = wv; r.querySelectorAll('button').forEach((x) => x.setAttribute('aria-pressed', String(x === b))); bildNeu(); this._aenderung(); });
            r.appendChild(b);
          }
          gruppe(name, r);
        }
      }
      return box.childElementCount ? box : null;
    }

    // M9 (fm8-fix4) / fm8-shop1 (H): Auf welchem Stiefelpaar sitzt das Rotkehlchen? Chips mit den Namen + „ohne“.
    // Vorgabe wie bisher das höchste Paar (kein Merkmal am Mitglied → Vertrag unverändert).
    _rotkehlchenFeld() {
      const r = this._feldRahmen('Rotkehlchen sitzt auf');
      r.classList.add('ttg-bk__field--vogel');
      this._vogelChips = el('div', 'ttg-bk__chips');
      this._vogelChips.setAttribute('role', 'group');
      this._vogelChips.setAttribute('aria-label', 'Rotkehlchen sitzt auf');
      r.appendChild(this._vogelChips);
      this._vogelFeld = r;
      this._vogelZeichnen();
      return r;
    }

    _vogelZeichnen() {
      const box = this._vogelChips;
      if (!box) return;
      const mg = this.cfg.mitglieder;
      const ms = this.state.mitglieder;
      box.textContent = '';
      // gleiche Regel wie die Szene: gewähltes Paar, sonst das höchste Stiefelpaar
      let hoechster = -1;
      ms.forEach((m, i) => { const t = mg.typen[m.typ]; if (t && t.art === 'stiefel' && (hoechster < 0 || t.oben < mg.typen[ms[hoechster].typ].oben)) hoechster = i; });
      const sitz = this.state.rotkehlchen ? vogelPlatz(this.cfg, ms, hoechster) : -1;
      const chip = (text, an, fn) => {
        const b = el('button', 'ttg-bk__chip', text); b.type = 'button';
        b.setAttribute('aria-pressed', String(an));
        b.addEventListener('click', () => { fn(); this._vogelZeichnen(); this._aenderung(); });
        box.appendChild(b);
      };
      ms.forEach((m, i) => {
        const t = mg.typen[m.typ];
        if (!t || t.art !== 'stiefel') return;
        chip((m.name || '').trim() || t.label, i === sitz, () => {
          this.state.rotkehlchen = true;
          ms.forEach((x, j) => { if (j === i && i !== hoechster) x.rotkehlchen = true; else delete x.rotkehlchen; });
        });
      });
      chip('ohne', !this.state.rotkehlchen, () => { this.state.rotkehlchen = false; ms.forEach((x) => delete x.rotkehlchen); });
    }


    // fm8-shop1 (D): Auswahl setzen (Mitglied oder Rotkehlchen). vonBuehne: Zeile/Chips öffnen, aufs Handy ins Bild scrollen.
    _waehle(a, vonBuehne) {
      const alt = this._aktiv;
      if (!vonBuehne && alt && a && alt.art === a.art && alt.i === a.i) return;
      this._aktiv = a ? { ...a, offen: !!vonBuehne } : null;
      this.querySelectorAll('.ttg-bk__member.is-aktiv, .ttg-bk__field--vogel.is-aktiv').forEach((n) => n.classList.remove('is-aktiv'));
      let ziel = null;
      if (a && a.art === 'mitglied') {
        ziel = this._mListe && this._mListe.querySelector('[data-bk-mitglied="' + a.i + '"]');
        if (ziel) {
          ziel.classList.add('is-aktiv');
          if (vonBuehne) { ziel.classList.remove('is-blink'); void ziel.offsetWidth; ziel.classList.add('is-blink'); setTimeout(() => ziel.classList.remove('is-blink'), 1400); }
        }
      } else if (a && a.art === 'rotkehlchen' && this._vogelFeld) {
        ziel = this._vogelFeld; ziel.classList.add('is-aktiv');
      }
      this._planeZeichnen();
      if (vonBuehne && ziel) {
        const imBild = () => { const r = ziel.getBoundingClientRect(); return r.top >= 0 && r.bottom <= innerHeight; };
        if (!imBild()) { try { ziel.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) { ziel.scrollIntoView(); } }
        const f = ziel.querySelector('.ttg-bk__typ[aria-pressed="true"], .ttg-bk__chip[aria-pressed="true"], .ttg-bk__chip');
        if (f) f.focus({ preventScroll: true });
      }
    }

    // fm8-shop1 (D): Tipp/Klick auf eine Figur in der Bühne → Auswahl dieses Mitglieds (bzw. Rotkehlchen-Chips)
    _buehneKlick() {
      const cv = this._canvas;
      if (!cv || !['reihe-bild', 'fuesse', 'reihe-figur', 'stiefel'].includes(this.cfg.anordnung)) return;
      const punkt = (e) => { const r = cv.getBoundingClientRect(); return [(e.clientX - r.left) / r.width * BK.W, (e.clientY - r.top) / r.height * BK.H]; };
      cv.addEventListener('click', (e) => {
        if (this._wischZeit && Date.now() - this._wischZeit < 400) return;   // war ein Wisch (Galerie)
        const sz = this._letzteSzene;
        const t = sz && trefferAn(sz.treffer, ...punkt(e));
        if (t && t.art === 'rotkehlchen' && !this._vogelFeld) t.art = 'mitglied';
        this._waehle(t, true);
      });
      cv.addEventListener('mousemove', (e) => {
        const sz = this._letzteSzene;
        cv.style.cursor = sz && trefferAn(sz.treffer, ...punkt(e)) ? 'pointer' : '';
      });
    }

    // fm8-shop3/shop4 (D): Namenslisten (M6 Menschen + Hunde, M7) — „Wie viele …?“ + nummerierte Namensfelder mit Menü ⋯;
    // neue Einträge kommen mit einem freien Beispielnamen (A).
    _namenBloecke({ schluessel, label, frage, wort, min, max, art }) {
      const r = this._feldRahmen(label);
      r.classList.add('ttg-bk__field--' + schluessel);
      const arr = this.state[schluessel];
      const liste = el('div', 'ttg-bk__members ttg-bk__blocks');
      const zeilen = () => {
        liste.textContent = '';
        arr.forEach((n, i) => {
          const titel = (i + 1) + '. ' + wort;
          const row = el('div', 'ttg-bk__member ttg-bk__block ttg-bk__block--name');
          const kopf = el('div', 'ttg-bk__block-kopf');
          kopf.append(el('span', 'ttg-bk__block-nr', titel), this._menue(titel, i, arr.length, {
            links: () => { [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]]; zeilen(); this._aenderung(); },
            rechts: () => { [arr[i + 1], arr[i]] = [arr[i], arr[i + 1]]; zeilen(); this._aenderung(); },
            weg: () => { arr.splice(i, 1); box(); zeilen(); this._aenderung(); },
            wegAus: arr.length <= min,
          }));
          const nf = el('div', 'ttg-bk__namefeld');
          nf.append(el('span', 'ttg-bk__sublabel', 'Name'), this._eingabe({ wert: n, max: this.cfg.regeln.namen_max, placeholder: 'z. B. ' + (BK_BEISPIEL[art][i] || ''), label: 'Name, ' + titel, weiter: true, beispiel: true, font: (this.cfg[schluessel] || {}).font,
            onInput: (v) => { arr[i] = v; this._aenderung(); } }));
          row.append(kopf, nf);
          liste.appendChild(row);
        });
      };
      const anzBox = el('div');
      const box = () => { anzBox.textContent = ''; anzBox.appendChild(this._anzahl(frage, min, max, arr.length, (n) => {
        while (arr.length < n) arr.push(this._neuerBeispielname(art)); while (arr.length > n) arr.pop(); zeilen(); this._aenderung(); })); };
      box(); zeilen();
      r.append(anzBox, liste, el('p', 'ttg-bk__hint', 'Max. ' + this.cfg.regeln.namen_max + ' Zeichen je Name.'));
      return r;
    }

    // fm8-shop3: M8 Rollen — „Wie viele Rollen?“ + nummerierte Blöcke (Rolle mit Idee-Knopf, Name, Menü ⋯)
    _rollenFeld() {
      const R = this.cfg.rollen;
      const r = this._feldRahmen(R.label || 'Rollen & Namen');
      const arr = this.state.rollen;
      const liste = el('div', 'ttg-bk__members ttg-bk__blocks');
      const zeilen = () => {
        liste.textContent = '';
        arr.forEach((ro, i) => {
          const titel = (i + 1) + '. Rolle';
          const row = el('div', 'ttg-bk__member ttg-bk__block');
          const kopf = el('div', 'ttg-bk__block-kopf');
          const insp = el('button', 'ttg-bk__insp', '💡 Idee'); insp.type = 'button'; insp.title = 'Rollen-Idee einsetzen';
          kopf.append(el('span', 'ttg-bk__block-nr', titel), insp, this._menue(titel, i, arr.length, {
            links: () => { [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]]; zeilen(); this._aenderung(); },
            rechts: () => { [arr[i + 1], arr[i]] = [arr[i], arr[i + 1]]; zeilen(); this._aenderung(); },
            weg: () => { arr.splice(i, 1); box(); zeilen(); this._aenderung(); },
            wegAus: arr.length <= 1,
          }));
          const fa = el('div', 'ttg-bk__namefeld');
          const a = this._eingabe({ wert: ro.rolle, max: this.cfg.regeln.rolle_max, placeholder: 'z. B. Leitwolf', label: 'Rolle, ' + titel, weiter: true, beispiel: true, font: (this.cfg.rollen.rolle || {}).font, onInput: (v) => { ro.rolle = v; this._aenderung(); } });
          fa.append(el('span', 'ttg-bk__sublabel', 'Rolle'), a);
          const fb = el('div', 'ttg-bk__namefeld');
          fb.append(el('span', 'ttg-bk__sublabel', 'Name'), this._eingabe({ wert: ro.name, max: this.cfg.regeln.namen_max, placeholder: 'z. B. Anja', label: 'Name, ' + titel, weiter: true, beispiel: true, font: (this.cfg.rollen.name || {}).font, onInput: (v) => { ro.name = v; this._aenderung(); } }));
          insp.addEventListener('click', () => {
            const ideen = R.inspiration || [];
            const vergeben = new Set(arr.map((x) => x.rolle));
            const frei = ideen.filter((x) => !vergeben.has(x));
            const pick = (frei.length ? frei : ideen)[Math.floor(Math.random() * (frei.length || ideen.length))];
            if (!pick) return;
            ro.rolle = pick; a._inp.value = pick; a._upd(); if (a._inp._bkBspPruef) a._inp._bkBspPruef(); this._aenderung();
          });
          row.append(kopf, fa, fb);
          liste.appendChild(row);
        });
      };
      const anzBox = el('div');
      const box = () => { anzBox.textContent = ''; anzBox.appendChild(this._anzahl('Wie viele Rollen?', 1, R.max, arr.length, (n) => {
        while (arr.length < n) { const rolle = beispielName([...BK_BEISPIEL.rolle, ...(R.inspiration || [])], this._vergebeneNamen()); if (rolle) this._bspMenge().add(rolle); arr.push({ rolle, name: this._neuerBeispielname('person') }); }
        while (arr.length > n) arr.pop(); zeilen(); this._aenderung(); })); };
      box(); zeilen();
      r.append(anzBox, liste, el('p', 'ttg-bk__hint', 'Rolle max. ' + this.cfg.regeln.rolle_max + ', Name max. ' + this.cfg.regeln.namen_max + ' Zeichen.'));
      return r;
    }

    _zaehlerAktualisieren() { /* Zähler aktualisieren sich je Eingabe selbst */ }

    // ---------- Produktfotos auf der Bühne (Muster ttg-family.js) ----------
    // fm8-shop1 (A): Hauptbild blätterbar — Bild 0 ist die Live-Vorschau (eigene Kachel vorn), danach die Produktfotos.
    // Handy: wischen; Desktop: Pfeile. Vorschaubilder darunter bleiben anklickbar.
    _galerie() {
      const track = this.querySelector('[data-bk-gal]');
      this._foto = this.querySelector('[data-bk-photo]');
      if (!track || !this._foto) return;
      const stage = this.querySelector('.ttg-bk__stage');
      const li = el('li');
      const vb = el('button', 'ttg-bk__gal-thumb ttg-bk__gal-thumb--vorschau');
      vb.type = 'button'; vb.dataset.bkGalVorschau = ''; vb.setAttribute('aria-pressed', 'true'); vb.setAttribute('aria-label', 'Deine Vorschau');
      const vc = document.createElement('canvas'); vc.width = 192; vc.height = 128; vb.appendChild(vc);
      li.appendChild(vb); track.prepend(li);
      this._galVc = vc;
      this._galTrack = track;
      this._randBeobachten(track.closest('.ttg-bk__gallery') || track);
      track.addEventListener('click', (e) => {
        const b = e.target.closest('[data-bk-gal-src], [data-bk-gal-vorschau]');
        if (!b) return;
        if (b.hasAttribute('data-bk-gal-vorschau') || b.getAttribute('aria-pressed') === 'true') { this._fotoAus(); return; }
        this._galZeige(this._galKnoepfe().indexOf(b));
      });
      if (this._galKnoepfe().length < 2 || !stage) return;
      const pfeil = (dir, text, cls) => {
        const b = el('button', 'ttg-bk__gal-pfeil ' + cls, text); b.type = 'button';
        b.setAttribute('aria-label', dir < 0 ? 'Vorheriges Bild' : 'Nächstes Bild');
        b.addEventListener('click', () => this._galSchritt(dir));
        stage.appendChild(b);
      };
      pfeil(-1, '‹', 'ttg-bk__gal-pfeil--zurueck');
      pfeil(1, '›', 'ttg-bk__gal-pfeil--vor');
      // Wischen über Touch-Events statt Pointer (23.10.2026): iOS Safari bricht bei touch-action:pan-y leicht schräge
      // Waagerecht-Gesten mit pointercancel ab. Ist die Geste klar waagerecht, sperrt touchmove sie und verhindert das Scrollen;
      // senkrecht gehört sie der Seite, zwei Finger (Zoom) verwerfen sie. Maus wischt nicht, Pfeile bleiben.
      let start = null;
      stage.addEventListener('touchstart', (e) => {
        const t = e.touches;
        start = t.length === 1 && !e.target.closest('.ttg-bk__gal-pfeil') ? { x: t[0].clientX, y: t[0].clientY, quer: false } : null;
      }, { passive: true });
      stage.addEventListener('touchmove', (e) => {
        if (!start) return;
        if (e.touches.length > 1) { start = null; return; }
        const dx = e.touches[0].clientX - start.x, dy = e.touches[0].clientY - start.y;
        if (!start.quer) {
          if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) start.quer = true;
          else if (Math.abs(dy) > 10) { start = null; return; }        // senkrecht: Seite scrollt
        }
        if (start.quer && e.cancelable) e.preventDefault();
      }, { passive: false });
      stage.addEventListener('touchend', (e) => {
        const s = start, t = e.changedTouches[0];
        start = null;
        if (!s || !s.quer || !t || e.touches.length) return;
        const dx = t.clientX - s.x;
        if (Math.abs(dx) > 45) { this._wischZeit = Date.now(); this._galSchritt(dx < 0 ? 1 : -1); }
      });
      stage.addEventListener('touchcancel', () => { start = null; });
    }

    _galKnoepfe() { return this._galTrack ? [...this._galTrack.querySelectorAll('[data-bk-gal-vorschau], [data-bk-gal-src]')] : []; }

    _galSchritt(dir) {
      const k = this._galKnoepfe();
      if (k.length < 2) return;
      const jetzt = Math.max(0, k.findIndex((x) => x.getAttribute('aria-pressed') === 'true'));
      this._galZeige((jetzt + dir + k.length) % k.length);
    }

    _galZeige(idx) {
      const k = this._galKnoepfe();
      const b = k[idx];
      if (!b) return;
      if (b.hasAttribute('data-bk-gal-vorschau')) { this._fotoAus(); return; }
      k.forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
      this._foto.src = b.dataset.bkGalSrc;
      this._foto.alt = b.dataset.bkGalAlt || '';
      this._foto.classList.add('is-an');
      this._foto.hidden = false;
      if (this._canvas) this._canvas.style.visibility = 'hidden';
      try { b.scrollIntoView({ block: 'nearest', inline: 'nearest' }); } catch (e) { /* alt */ }
    }

    _galVorschauBlit() {
      const vc = this._galVc;
      if (!vc || !this._canvas || this._canvas.width < 10) return;
      vc.getContext('2d').drawImage(this._canvas, 0, 0, vc.width, vc.height);
    }

    _fotoAus() {
      if (!this._foto || !this._foto.classList.contains('is-an')) return;
      this._foto.classList.remove('is-an'); this._foto.hidden = true; this._foto.removeAttribute('src');
      if (this._canvas) this._canvas.style.visibility = '';
      this._galKnoepfe().forEach((x) => x.setAttribute('aria-pressed', String(x.hasAttribute('data-bk-gal-vorschau'))));
    }

    // Info-Blöcke des main-Abschnitts unter den Kaufknopf (wie ttg-family.js _adoptInfoBlocks)
    _infoBloecke() {
      const ziel = this.querySelector('[data-bk-info]');
      const details = document.querySelector('.product-information .product-details');
      if (!ziel || !details) return;
      try {
        const zuege = [];
        const pay = details.querySelector('.payment-icons');
        if (pay) zuege.push(pay);
        const check = [...details.querySelectorAll('.text-block')].find((n) => (n.textContent || '').includes('✔'));
        let gruppe = check ? check.closest('.group-block') : null;
        while (gruppe) {
          const p = gruppe.parentElement ? gruppe.parentElement.closest('.group-block') : null;
          if (p && !p.querySelector('.payment-icons') && !p.querySelector('variant-picker') && !p.querySelector('.accordion') && !p.querySelector('h1')) { gruppe = p; continue; }
          break;
        }
        if (gruppe) zuege.push(gruppe);
        const acc = details.querySelector('.accordion');
        if (acc) zuege.push(acc);
        for (const n of zuege) ziel.appendChild(n);
        if (zuege.length) ziel.hidden = false;
      } catch (e) { console.warn('[ttg-baukasten] Info-Blöcke nicht umgezogen:', e); }
    }

    // Schwebende Mini-Vorschau (Mobil), wenn die Bühne aus dem Bild ist — Muster ttg-family.js
    _mini() {
      if (typeof IntersectionObserver === 'undefined' || !this._canvas) return;
      const stage = this.querySelector('.ttg-bk__stage');
      if (!stage) return;
      const mq = window.matchMedia('(max-width: 749px)');
      const wrap = el('div', 'ttg-bk__mini');
      wrap.setAttribute('aria-hidden', 'true');
      const mc = document.createElement('canvas');
      wrap.appendChild(mc);
      const header = document.getElementById('header-component');
      if (header && header.hasAttribute('sticky')) {
        const h = header.getBoundingClientRect().height;
        if (h > 0) wrap.style.setProperty('--ttg-bk-mini-top', Math.round(h + 8) + 'px');
      }
      this.appendChild(wrap);
      this._miniZ = { wrap, canvas: mc, an: false, t: 0 };
      this._miniGesten(wrap, stage);
      const io = new IntersectionObserver((entries) => {
        const e = entries[entries.length - 1];
        clearTimeout(this._miniZ.t);
        this._miniZ.t = setTimeout(() => {
          if (!mq.matches) { this._miniSchalten(false); return; }
          if (!e.isIntersecting) this._miniSchalten(true);
          else if (e.intersectionRatio >= 0.35) this._miniSchalten(false);
        }, 120);
      }, { threshold: [0, 0.35] });
      io.observe(stage);
    }

    // fm8-shop1 (C): Mini-Vorschau mit einem Finger verschieben (rastet links/rechts ein), mit zwei Fingern zoomen (1–2,5×),
    // Doppeltipp setzt den Zoom zurück, einfacher Tipp springt zur Bühne. Muster: Engine _initMiniPreview (dort NICHTS geändert);
    // anders als dort bleibt der Zoom stehen, bis doppelt getippt wird (Briefing fm8-shop1 C).
    _miniGesten(wrap, stage) {
      const z = this._miniZ;
      Object.assign(z, { dx: 0, dy: 0, scale: 1, drag: null, pinch: null, gesture: null, letzterTipp: 0, tippT: 0, raf: 0 });
      const RAND = 12, MAX = 2.5;
      const pts = new Map();
      const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), Math.max(lo, hi));
      const dist = () => { const p = [...pts.values()]; return Math.hypot(p[1].x - p[0].x, p[1].y - p[0].y) || 1; };
      const vw = () => (window.visualViewport ? window.visualViewport.width : innerWidth);
      const vh = () => (window.visualViewport ? window.visualViewport.height : innerHeight);
      // fm8-shop3 (F): bei offener Bildschirmtastatur verschiebt der Browser den sichtbaren Ausschnitt — Versatz einrechnen (wie Engine)
      const vv = window.visualViewport || null;
      const apply = () => { wrap.style.transform = 'translate3d(' + (z.dx + (vv ? vv.offsetLeft : 0)) + 'px,' + (z.dy + (vv ? vv.offsetTop : 0)) + 'px,0)' + (z.scale !== 1 ? ' scale(' + z.scale + ')' : ''); };
      const plan = () => { if (!z.raf) z.raf = requestAnimationFrame(() => { z.raf = 0; apply(); }); };
      const heim = () => ({ l: wrap.offsetLeft, t: wrap.offsetTop, w: wrap.offsetWidth, h: wrap.offsetHeight });
      const einrasten = () => {
        const h0 = heim();
        const links = h0.l + z.dx + h0.w * z.scale / 2 < vw() / 2;
        z.dx = links ? 0 : vw() - h0.w * z.scale - RAND - h0.l;
        z.dy = clamp(h0.t + z.dy, h0.t, vh() - h0.h * z.scale - RAND) - h0.t;
        wrap.style.transformOrigin = (links ? 'left' : 'right') + ' top';
        if (!links) z.dx = vw() - h0.w - RAND - h0.l;              // Ursprung rechts: Kachelrand bleibt am Rand
        wrap.classList.add('is-settling'); apply();
        setTimeout(() => wrap.classList.remove('is-settling'), 220);
      };
      this._miniEinrasten = einrasten;
      const tipp = () => {
        const jetzt = Date.now();
        if (jetzt - z.letzterTipp < 320) {                            // Doppeltipp: Zoom zurück
          clearTimeout(z.tippT); z.letzterTipp = 0;
          z.scale = 1; wrap.classList.add('is-settling'); apply(); setTimeout(() => wrap.classList.remove('is-settling'), 220);
          return;
        }
        z.letzterTipp = jetzt;
        z.tippT = setTimeout(() => { z.letzterTipp = 0; stage.scrollIntoView({ block: 'center', behavior: 'smooth' }); }, 330);
      };
      wrap.addEventListener('pointerdown', (e) => {
        if (!z.an) return;
        e.preventDefault();
        if (e.isPrimary) { pts.clear(); z.drag = null; z.pinch = null; }
        pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        try { wrap.setPointerCapture(e.pointerId); } catch (err) { /* egal */ }
        if (pts.size === 1) z.drag = { px: e.clientX, py: e.clientY, sx: z.dx, sy: z.dy, weg: false };
        else if (pts.size === 2) { z.drag = null; z.pinch = { d0: dist(), base: z.scale }; wrap.classList.add('is-zooming'); }
      });
      wrap.addEventListener('pointermove', (e) => {
        if (pts.has(e.pointerId)) pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (z.pinch) { if (pts.size >= 2) { z.scale = clamp(z.pinch.base * dist() / z.pinch.d0, 1, MAX); plan(); } return; }
        if (!z.drag) return;
        const ddx = e.clientX - z.drag.px, ddy = e.clientY - z.drag.py;
        if (Math.abs(ddx) + Math.abs(ddy) > 6) z.drag.weg = true;
        z.dx = z.drag.sx + ddx; z.dy = z.drag.sy + ddy; plan();
      });
      const hoch = (e) => {
        pts.delete(e.pointerId);
        if (z.pinch) { if (pts.size < 2) { z.pinch = null; wrap.classList.remove('is-zooming'); einrasten(); } return; }
        if (!z.drag) return;
        const weg = z.drag.weg;
        z.drag = null;
        if (weg) einrasten(); else if (e.type === 'pointerup') tipp();
      };
      wrap.addEventListener('pointerup', hoch);
      wrap.addEventListener('pointercancel', hoch);
      // iOS Safari: Zwei-Finger-Geste auf der Kachel nicht zum Seiten-Zoom werden lassen (wie Engine)
      const np = { passive: false };
      const mehr = (e) => { if (e.touches && e.touches.length > 1) e.preventDefault(); };
      wrap.addEventListener('touchstart', mehr, np);
      wrap.addEventListener('touchmove', mehr, np);
      wrap.addEventListener('gesturestart', (e) => { if (!z.an) return; e.preventDefault(); z.drag = null; if (!z.pinch) z.gesture = { base: z.scale }; }, np);
      wrap.addEventListener('gesturechange', (e) => { if (!z.an) return; e.preventDefault(); if (z.pinch) return; if (!z.gesture) z.gesture = { base: z.scale / (e.scale || 1) }; z.scale = clamp(z.gesture.base * (e.scale || 1), 1, MAX); plan(); }, np);
      wrap.addEventListener('gestureend', (e) => { if (!z.an) return; e.preventDefault(); pts.clear(); if (z.gesture) { z.gesture = null; einrasten(); } }, np);
      window.addEventListener('resize', () => { if (z.an && !z.drag && !z.pinch) einrasten(); });
      if (vv) {
        vv.addEventListener('scroll', () => { if (z.an && !z.drag && !z.pinch) apply(); });
        vv.addEventListener('resize', () => { if (z.an && !z.drag && !z.pinch) einrasten(); });
      }
    }

    _miniSchalten(an) {
      if (!this._miniZ || this._miniZ.an === an) return;
      this._miniZ.an = an;
      this._miniZ.wrap.classList.toggle('is-an', an);
      if (an) this._miniBlit();
    }

    _miniBlit() {
      const m = this._miniZ;
      if (!m || !m.an || !this._canvas || this._canvas.width < 10) return;
      const w = 480, h = Math.round(w * this._canvas.height / this._canvas.width);
      if (m.canvas.width !== w || m.canvas.height !== h) { m.canvas.width = w; m.canvas.height = h; }
      m.canvas.getContext('2d').drawImage(this._canvas, 0, 0, w, h);
    }
  }
  customElements.define('ttg-baukasten', TTGBaukasten);
}

if (typeof window !== 'undefined' && typeof customElements !== 'undefined' && !customElements.get('ttg-baukasten')) {
  defineBaukasten();
}
